import { routeTrackingService } from '../services/RouteTrackingService.js';
import { canPurchaseInApp } from '../services/purchase.js';
import { isSegmentOnRoute } from '../services/RoadQualityService.js';
import { prepareWindWaypoints, fetchWindHotspots } from '../services/windWaypoints.js';
import { showSignInPrompt } from '../ui/SignInPromptModal.js';
import L from '../compat/LeafletShim.js';
import { w, hasUsOnlyData, displayLocale, currentMarket } from '../services/market.js';
import * as units from '../services/units.js';

// For text from outside sources (OpenStreetMap names) placed into HTML.
const escapeHtml = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

export class UIController {
    constructor(services) {
        this.mapService = services.mapService;
        this.elevationService = services.elevationService;
        this.windService = services.windService;
        this.routeService = services.routeService;
        this.gasStationService = services.gasStationService;
        this.clearanceService = services.clearanceService;
        this.roadQualityService = services.roadQualityService;
        this.campgroundService = services.campgroundService;
        
        this.currentRoute = null;
        this.currentRoutes = null;
        this.bestRouteIndex = 0;
        this.selectedRouteIndex = undefined;
        
        // Store stops per route: { routeIndex: [stops] }
        this.routeStopsMap = {};

        // Cities to route around (preferences.avoid). Unlike stops, this is
        // a standing trip-wide preference, not cleared on recalculation --
        // same array reference handed to routeService so there's nothing to
        // resync. Also forwarded into navigation's overlays at nav-start
        // (main.js's startZephyrNavigation) so native reroutes keep
        // avoiding them -- see ZephyrRouteProvider.kt / ZephyrNavigationActivity.kt.
        this.avoidCities = [];
        if (this.routeService) this.routeService.avoidCities = this.avoidCities;

        // Cities to force the route through (preferences.use), picked by
        // search rather than tapping a map pin -- a separate list from the
        // existing pin-based routeStopsMap/addStopToRoute system (which is
        // per-route-index and recalculates immediately on add). This one
        // follows the same standing-preference pattern as avoidCities above:
        // persists across recalculation, applied on the next Calculate
        // Route click, and merged into the SAME native "stops" list at
        // nav-start (main.js) since a forced waypoint that drops out once
        // passed is exactly stops' existing semantics -- no native changes
        // needed for this to also hold on reroute.
        this.throughCities = [];
        if (this.routeService) this.routeService.throughCities = this.throughCities;

        // Phase 27: reverse-geocode results keyed by exact "lat,lng" -- the
        // trip summary re-resolves the same start/stop/destination coords on
        // every rebuild with no caching, which is fine for a one-off
        // calculation but turns into repeated identical hits against
        // Nominatim's public (rate-limited) API once something re-renders
        // the summary often, like the departure time slider below.
        this.geocodeCache = new Map();
    }

    init() {
        console.log('Initializing UI Controller...');
        
        // Expose globally for MapService to access
        window.uiController = this;
        
        // Initialize map
        try {
            this.mapService.init();
            console.log('Map initialized successfully');
        } catch (error) {
            console.error('Error initializing map:', error);
        }

        // Set up event listeners
        this.setupEventListeners();
        
        // Expose function for adding gas stations as stops
        window.addGasStationAsStop = (lat, lng, name, markerId) => {
            this.addStopToRoute(lat, lng, name, markerId);
        };
        
        // Expose function for removing gas station stops
        window.removeGasStationStop = async (stopIndex, markerId) => {
            console.log(`Removing stop at index ${stopIndex}`);
            
            // Close the popup
            if (markerId) {
                const marker = this.mapService.gasStationMarkers.find(m => m._customId === markerId);
                if (marker) {
                    marker.closePopup();
                }
            }
            
            // Remove the stop, then route again through the stops that are left.
            this.removeStopFromCurrentRoute(stopIndex);
            {
                const ri = this.selectedRouteIndex !== undefined ? this.selectedRouteIndex : this.bestRouteIndex;
                this.recalculateThroughStops(this.getRouteStops(ri));
                return;
            }
            
            // Update trip summary
            await this.updateTripSummary();
            
            // Rebuild the marker's popup to show "Add as Stop" again (marker stays on map)
            if (markerId) {
                this.mapService.rebuildGasStationPopup(markerId);
            }
            
            // Recalculate wind profile if wind icons are shown
            const showWindIcons = document.getElementById('showWindIcons').checked;
            if (showWindIcons) {
                const routeIndex = this.selectedRouteIndex !== undefined ? this.selectedRouteIndex : this.bestRouteIndex;
                const route = this.currentRoutes[routeIndex];
                
                if (route && route.timeAwareWindProfile) {
                    console.log('🌬️ Recalculating wind profile after stop removal...');
                    
                    // Get departure date/time from form inputs
                    const departureDateInput = document.getElementById('departureDate');
                    const departureTimeInput = document.getElementById('departureTime');
                    
                    let departureDateTime = new Date();
                    if (departureDateInput && departureDateInput.value) {
                        const dateStr = departureDateInput.value;
                        const timeStr = departureTimeInput && departureTimeInput.value ? departureTimeInput.value : '08:00';
                        departureDateTime = new Date(`${dateStr}T${timeStr}`);
                    }
                    
                    // Get RV specs from profile
                    const rvSpecs = {
                        type: window.currentProfile?.rvType || 'travel-trailer',
                        height: window.currentProfile?.height || 10,
                        weight: window.currentProfile?.weight || 5000,
                        length: window.currentProfile?.length || 25
                    };
                    
                    // Prepare waypoints without the removed stop
                    const waypoints = this.prepareWaypointsForWindForecast(route.coordinates, routeIndex, route.pinnedWindPoint);
                    
                    // Rebuild time-aware wind profile
                    const timeAwareWindProfile = await this.windService.buildRouteWindProfile(
                        departureDateTime,
                        waypoints,
                        rvSpecs,
                        window.travelPreferences || {}
                    );
                    
                    // Update the route's wind profile
                    route.timeAwareWindProfile = timeAwareWindProfile;
                    
                    // Re-add wind markers with updated times
                    this.mapService.addWindMarkers(timeAwareWindProfile.windProfile);
                    
                    console.log('✓ Wind profile recalculated after stop removal');
                }
            }
        };

        console.log('UI Controller initialized');
    }

    setupEventListeners() {
        console.log('Setting up event listeners...');
        const calculateBtn = document.getElementById('calculateRoute');
        
        if (!calculateBtn) {
            console.error('Calculate button not found!');
            return;
        }
        
        console.log('Calculate button found, adding click listener');
        calculateBtn.addEventListener('click', () => {
            console.log('Button clicked!');
            this.handleCalculateRoute();
        });

        // Add enter key support for inputs
        ['start', 'destination'].forEach(id => {
            const input = document.getElementById(id);
            if (input) {
                input.addEventListener('keypress', (e) => {
                    if (e.key === 'Enter') {
                        this.handleCalculateRoute();
                    }
                });
            }
        });
        
        // Show/hide tow vehicle section based on RV type
        const rvTypeSelect = document.getElementById('rvType');
        const towVehicleSection = document.getElementById('towVehicleSection');
        
        if (rvTypeSelect && towVehicleSection) {
            rvTypeSelect.addEventListener('change', (e) => {
                const isTruckOnly = e.target.value === 'truck-only';
                const isTrailer = ['fifth-wheel', 'travel-trailer'].includes(e.target.value);
                if (isTrailer || isTruckOnly) {
                    towVehicleSection.classList.remove('hidden');
                } else {
                    towVehicleSection.classList.add('hidden');
                }
                // Hide sway control for fifth-wheels (only travel trailers need it)
                const swayGroup = document.getElementById('swayControlGroup');
                if (swayGroup) {
                    swayGroup.style.display = e.target.value === 'fifth-wheel' ? 'none' : 'block';
                }
            });
        }
        
        // Show/hide gas station options
        const showGasStations = document.getElementById('showGasStations');
        const gasStationOptions = document.getElementById('gasStationOptions');
        
        if (showGasStations && gasStationOptions) {
            showGasStations.addEventListener('change', async (e) => {
                if (e.target.checked) {
                    gasStationOptions.classList.remove('hidden');
                    
                    // Re-fetch and display gas stations for the current route
                    if (this.currentRoutes && this.currentRoutes.length > 0) {
                        const routeIndex = this.selectedRouteIndex !== undefined ? this.selectedRouteIndex : this.bestRouteIndex;
                        const route = this.currentRoutes[routeIndex];
                        
                        if (route && route.coordinates) {
                            console.log('🔍 Fetching gas stations for route', routeIndex + 1);
                            
                            // Get stops for this route to calculate from last stop
                            const routeStops = this.getRouteStops(routeIndex);
                            let startCoords = route.coordinates;
                            
                            if (routeStops.length > 0) {
                                const lastStop = routeStops[routeStops.length - 1];
                                const lastStopIndex = route.coordinates.findIndex(coord => 
                                    Math.abs(coord[0] - lastStop.lat) < 0.0001 && 
                                    Math.abs(coord[1] - lastStop.lng) < 0.0001
                                );
                                if (lastStopIndex !== -1) {
                                    startCoords = route.coordinates.slice(lastStopIndex);
                                }
                            }
                            
                            await this.displayGasStations({ coordinates: startCoords });
                            console.log('✓ Gas stations displayed');
                        }
                    }
                } else {
                    gasStationOptions.classList.add('hidden');
                    // Clear gas station markers when unchecked
                    console.log('🗑️ Clearing gas station markers');
                    this.gasStationRequest = (this.gasStationRequest || 0) + 1; // a search still running must not redraw them
                    this.mapService.clearGasStationMarkers();
                }
            });

            // Phase 16: Multi-select checkbox interaction logic
            const fuelChecks = document.querySelectorAll('.fuel-chain-check');
            fuelChecks.forEach(check => {
                check.addEventListener('change', (e) => {
                    const allCheck = document.querySelector('.fuel-chain-check[value="all"]');
                    const individualChecks = document.querySelectorAll('.fuel-chain-check:not([value="all"])');

                    if (e.target.value === 'all' && e.target.checked) {
                        // "All" checked → uncheck all individuals
                        individualChecks.forEach(cb => cb.checked = false);
                    } else if (e.target.value !== 'all' && e.target.checked) {
                        // Individual checked → uncheck "All"
                        allCheck.checked = false;
                    }

                    // Prevent empty state — if nothing checked, re-check "All"
                    const anyChecked = Array.from(document.querySelectorAll('.fuel-chain-check:checked')).length > 0;
                    if (!anyChecked) {
                        allCheck.checked = true;
                    }

                    // Auto-refresh gas stations if route exists
                    if (this.currentRoutes && this.currentRoutes.length > 0) {
                        const routeIndex = this.selectedRouteIndex !== undefined ? this.selectedRouteIndex : this.bestRouteIndex;
                        const route = this.currentRoutes[routeIndex];
                        if (route && route.coordinates) {
                            this.displayGasStations({ coordinates: route.coordinates });
                        }
                    }
                });
            });
        }
        
        // Toggle wind icons on/off
        const showWindIcons = document.getElementById('showWindIcons');
        if (showWindIcons) {
            showWindIcons.addEventListener('change', (e) => {
                if (e.target.checked) {
                    // Show wind markers for the currently selected route
                    if (!this.currentRoutes || this.currentRoutes.length === 0) {
                        console.log('⚠️ No routes available to show wind markers');
                        return;
                    }
                    
                    const routeIndex = this.selectedRouteIndex !== undefined ? this.selectedRouteIndex : this.bestRouteIndex;
                    const selectedRoute = this.currentRoutes[routeIndex];
                    
                    if (selectedRoute && selectedRoute.timeAwareWindProfile) {
                        console.log('🌬️ Showing wind markers for route', routeIndex + 1);
                        this.mapService.addWindMarkers(selectedRoute.timeAwareWindProfile.windProfile);
                    } else {
                        console.log('⚠️ No wind data available for selected route');
                    }
                } else {
                    // Hide wind markers
                    console.log('🌬️ Hiding wind markers');
                    this.mapService.windMarkers.forEach(marker => marker.remove());
                    this.mapService.windMarkers = [];
                }
            });
        }
        
        // Profile management (disabled - feature not yet implemented)
        // this.setupProfileManagement();
        
        console.log('Event listeners set up successfully');
    }

    setupProfileManagement() {
        console.log('Setting up profile management...');
        const ProfileService = window.ProfileService;
        if (!ProfileService) {
            console.error('ProfileService not available');
            return;
        }
        console.log('ProfileService loaded successfully');

        const profileSelect = document.getElementById('profileSelect');
        const newProfileBtn = document.getElementById('newProfileBtn');
        const saveProfileBtn = document.getElementById('saveProfileBtn');
        const deleteProfileBtn = document.getElementById('deleteProfileBtn');

        if (!profileSelect || !newProfileBtn || !saveProfileBtn || !deleteProfileBtn) {
            console.error('Profile UI elements not found:', {
                profileSelect: !!profileSelect,
                newProfileBtn: !!newProfileBtn,
                saveProfileBtn: !!saveProfileBtn,
                deleteProfileBtn: !!deleteProfileBtn
            });
            return;
        }
        console.log('All profile UI elements found');

        // Load profiles into dropdown
        this.refreshProfileList();

        // Load active profile if exists
        const activeProfile = ProfileService.getActiveProfile();
        if (activeProfile) {
            const activeName = ProfileService.getActiveProfileName();
            profileSelect.value = activeName;
            ProfileService.loadProfileIntoForm(activeProfile);
            console.log('Loaded active profile:', activeName);
        }

        // Profile selection
        profileSelect.addEventListener('change', (e) => {
            console.log('Profile selected:', e.target.value);
            const profileName = e.target.value;
            if (profileName) {
                const profile = ProfileService.loadProfile(profileName);
                if (profile) {
                    ProfileService.loadProfileIntoForm(profile);
                    ProfileService.setActiveProfile(profileName);
                }
            } else {
                ProfileService.setActiveProfile(null);
            }
        });

        // New profile - open modal
        newProfileBtn.addEventListener('click', () => {
            console.log('New profile button clicked - opening modal');
            this.openProfileModal();
        });
        
        // Setup modal event handlers
        this.setupModalHandlers();

        // Save profile
        saveProfileBtn.addEventListener('click', () => {
            console.log('Save profile button clicked');
            const profileName = profileSelect.value;

            if (!profileName) {
                alert('Please create a new profile first by clicking "+ New"');
                return;
            }

            const profile = ProfileService.createProfileFromForm();
            
            if (ProfileService.saveProfile(profileName, profile)) {
                // Visual feedback
                saveProfileBtn.textContent = '✓ Saved';
                saveProfileBtn.style.background = '#4CAF50';
                saveProfileBtn.style.color = 'white';
                saveProfileBtn.style.borderColor = '#4CAF50';
                
                setTimeout(() => {
                    saveProfileBtn.textContent = 'Save';
                    saveProfileBtn.style.background = '';
                    saveProfileBtn.style.color = '';
                    saveProfileBtn.style.borderColor = '';
                }, 1000);
                
                console.log('Profile updated:', profileName);
            } else {
                alert('Error saving profile');
                console.error('Failed to save profile');
            }
        });

        // Delete profile
        deleteProfileBtn.addEventListener('click', () => {
            console.log('Delete profile button clicked');
            const profileName = profileSelect.value;
            if (!profileName) {
                alert('Please select a profile to delete');
                return;
            }

            if (confirm(`Delete profile "${profileName}"?`)) {
                if (ProfileService.deleteProfile(profileName)) {
                    this.refreshProfileList();
                    profileSelect.value = '';
                    alert(`Profile "${profileName}" deleted`);
                    console.log('Profile deleted:', profileName);
                } else {
                    alert('Error deleting profile');
                    console.error('Failed to delete profile');
                }
            }
        });
        
        console.log('Profile management setup complete');
    }

    setupModalHandlers() {
        const modal = document.getElementById('profileModal');
        const closeModalBtn = document.getElementById('closeModal');
        const cancelModalBtn = document.getElementById('cancelModal');
        const saveModalBtn = document.getElementById('saveModal');
        const modalRvType = document.getElementById('modalRvType');
        const modalTowVehicleSection = document.getElementById('modalTowVehicleSection');
        const modalHasRV = document.getElementById('modalHasRV');
        const modalRvSpecsSection = document.getElementById('modalRvSpecsSection');

        // Close modal handlers
        closeModalBtn.addEventListener('click', () => this.closeProfileModal());
        cancelModalBtn.addEventListener('click', () => this.closeProfileModal());
        
        // Close modal when clicking outside
        modal.addEventListener('click', (e) => {
            if (e.target === modal) {
                this.closeProfileModal();
            }
        });

        // Toggle RV specs section
        modalHasRV.addEventListener('change', (e) => {
            if (e.target.checked) {
                modalRvSpecsSection.classList.remove('disabled');
                // Show tow vehicle section if trailer type is selected
                const isTrailer = ['fifth-wheel', 'travel-trailer'].includes(modalRvType.value);
                if (isTrailer) {
                    modalTowVehicleSection.classList.remove('disabled');
                }
            } else {
                modalRvSpecsSection.classList.add('disabled');
                // Always show tow vehicle section when RV is disabled (truck-only mode)
                modalTowVehicleSection.classList.remove('disabled');
            }
        });

        // Show/hide tow vehicle section in modal based on RV type
        modalRvType.addEventListener('change', (e) => {
            const hasRV = modalHasRV.checked;
            const isTrailer = ['fifth-wheel', 'travel-trailer'].includes(e.target.value);
            
            if (!hasRV) {
                // Truck-only mode: always show tow vehicle section
                modalTowVehicleSection.classList.remove('disabled');
            } else if (isTrailer) {
                // Trailer mode: show tow vehicle section
                modalTowVehicleSection.classList.remove('disabled');
            } else {
                // Motorhome mode: hide tow vehicle section
                modalTowVehicleSection.classList.add('disabled');
            }
        });

        // Save profile from modal
        saveModalBtn.addEventListener('click', () => this.saveProfileFromModal());
        
        // Allow Enter key to submit in profile name field
        document.getElementById('modalProfileName').addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                this.saveProfileFromModal();
            }
        });
    }

    openProfileModal() {
        const modal = document.getElementById('profileModal');
        
        // Reset modal form to defaults
        document.getElementById('modalProfileName').value = '';
        document.getElementById('modalHasRV').checked = true;
        document.getElementById('modalRvType').value = 'class-c';
        document.getElementById('modalHeight').value = 12;
        document.getElementById('modalWeight').value = 15000;
        document.getElementById('modalLength').value = 30;
        
        // Reset tow vehicle fields
        document.getElementById('modalTowVehicleType').value = '';
        document.getElementById('modalTowVehicleEngine').value = '';
        document.getElementById('modalTowCapacity').value = '';
        document.getElementById('modalTowVehicleWeight').value = '';
        document.getElementById('modalHasSwayControl').checked = false;
        document.getElementById('modalHasTrailerBrakes').checked = false;
        
        // Enable RV specs section, disable tow vehicle section initially
        document.getElementById('modalRvSpecsSection').classList.remove('disabled');
        document.getElementById('modalTowVehicleSection').classList.add('disabled');
        
        // Show modal
        modal.classList.remove('hidden');
        
        // Focus on profile name input
        setTimeout(() => {
            document.getElementById('modalProfileName').focus();
        }, 100);
    }

    closeProfileModal() {
        const modal = document.getElementById('profileModal');
        modal.classList.add('hidden');
    }

    saveProfileFromModal() {
        const ProfileService = window.ProfileService;
        const saveModalBtn = document.getElementById('saveModal');
        
        // Prevent double-submission
        if (saveModalBtn.disabled) {
            console.log('Button already disabled, preventing double-submission');
            return;
        }
        
        const profileName = document.getElementById('modalProfileName').value.trim();
        
        // Validate profile name
        if (!profileName) {
            alert('Please enter a profile name');
            document.getElementById('modalProfileName').focus();
            return;
        }
        
        // Check if profile already exists
        const existingProfiles = ProfileService.getProfileNames();
        console.log('Checking for duplicates. Existing profiles:', existingProfiles);
        console.log('New profile name:', profileName);
        
        if (existingProfiles.includes(profileName)) {
            console.log('DUPLICATE DETECTED:', profileName);
            alert(`Profile "${profileName}" already exists. Please choose a different name.`);
            document.getElementById('modalProfileName').focus();
            return;
        }
        
        console.log('No duplicate found, proceeding to save...');
        
        // Disable button to prevent double-clicks
        saveModalBtn.disabled = true;
        saveModalBtn.textContent = 'Creating...';
        
        // Check if this is a truck-only profile or includes RV
        const hasRV = document.getElementById('modalHasRV').checked;
        const profile = {};
        
        if (hasRV) {
            // Create profile with RV specs
            const rvType = document.getElementById('modalRvType').value;
            profile.type = rvType;
            profile.height = parseFloat(document.getElementById('modalHeight').value);
            profile.weight = parseFloat(document.getElementById('modalWeight').value);
            profile.length = parseFloat(document.getElementById('modalLength').value);
            
            // Add tow vehicle if applicable (for trailers)
            const isTrailer = ['fifth-wheel', 'travel-trailer'].includes(rvType);
            if (isTrailer) {
                const towVehicleType = document.getElementById('modalTowVehicleType').value;
                const towVehicleEngine = document.getElementById('modalTowVehicleEngine').value;
                const towCapacity = document.getElementById('modalTowCapacity').value;
                const towVehicleWeight = document.getElementById('modalTowVehicleWeight').value;
                
                profile.towVehicle = {
                    type: towVehicleType || null,
                    engine: towVehicleEngine || null,
                    capacity: towCapacity ? parseFloat(towCapacity) : null,
                    weight: towVehicleWeight ? parseFloat(towVehicleWeight) : null,
                    hasSwayControl: document.getElementById('modalHasSwayControl').checked,
                    hasTrailerBrakes: document.getElementById('modalHasTrailerBrakes').checked
                };
            }
        } else {
            // Truck-only profile (no RV/trailer)
            profile.type = 'truck-only';
            profile.truckOnly = true;
            
            // Get tow vehicle details
            const towVehicleType = document.getElementById('modalTowVehicleType').value;
            const towVehicleEngine = document.getElementById('modalTowVehicleEngine').value;
            const towCapacity = document.getElementById('modalTowCapacity').value;
            const towVehicleWeight = document.getElementById('modalTowVehicleWeight').value;
            
            profile.towVehicle = {
                type: towVehicleType || null,
                engine: towVehicleEngine || null,
                capacity: towCapacity ? parseFloat(towCapacity) : null,
                weight: towVehicleWeight ? parseFloat(towVehicleWeight) : null,
                hasSwayControl: document.getElementById('modalHasSwayControl').checked,
                hasTrailerBrakes: document.getElementById('modalHasTrailerBrakes').checked
            };
            
            // Set minimal RV specs for truck-only (used for route calculations)
            profile.height = 7; // Typical truck height
            profile.weight = profile.towVehicle.weight || 6000;
            profile.length = 20; // Typical truck length
        }
        
        // Save profile
        if (ProfileService.saveProfile(profileName, profile)) {
            console.log('Profile created from modal:', profileName);
            
            // Load profile into main form
            ProfileService.loadProfileIntoForm(profile);
            ProfileService.setActiveProfile(profileName);
            
            // Refresh profile list and select new profile
            this.refreshProfileList();
            document.getElementById('profileSelect').value = profileName;
            
            // Close modal
            this.closeProfileModal();
            
            // Re-enable button
            saveModalBtn.disabled = false;
            saveModalBtn.textContent = 'Create Profile';
            
            // Visual feedback on the New button
            const newProfileBtn = document.getElementById('newProfileBtn');
            newProfileBtn.textContent = '✓ Created';
            newProfileBtn.style.background = '#4CAF50';
            newProfileBtn.style.color = 'white';
            newProfileBtn.style.borderColor = '#4CAF50';
            
            setTimeout(() => {
                newProfileBtn.textContent = '+ New';
                newProfileBtn.style.background = '';
                newProfileBtn.style.color = '';
                newProfileBtn.style.borderColor = '';
            }, 2000);
        } else {
            alert('Error saving profile');
            console.error('Failed to save profile from modal');
            
            // Re-enable button on error
            saveModalBtn.disabled = false;
            saveModalBtn.textContent = 'Create Profile';
        }
    }

    refreshProfileList() {
        const ProfileService = window.ProfileService;
        const profileSelect = document.getElementById('profileSelect');
        const currentValue = profileSelect.value;

        // Clear existing options except the first one
        profileSelect.innerHTML = '<option value="">-- New Profile (unsaved) --</option>';

        // Add all profiles
        const profileNames = ProfileService.getProfileNames();
        profileNames.forEach(name => {
            const option = document.createElement('option');
            option.value = name;
            option.textContent = name;
            profileSelect.appendChild(option);
        });

        // Restore selection if it still exists
        if (profileNames.includes(currentValue)) {
            profileSelect.value = currentValue;
        }
        
        console.log('Profile list refreshed, found', profileNames.length, 'profiles');
    }

    /**
     * Prepare waypoints with distance information for wind forecast
     * Includes user-added stops with 30-minute stop time
     * @param {Array} coordinates - Route coordinates
     * @param {Number} routeIndex - Index of the route to get stops for
     * @returns {Array} Waypoints with distance from previous point and stop info
     */
    prepareWaypointsForWindForecast(coordinates, routeIndex = null, pinnedWindPoint = null, hotspots = []) {
        return prepareWindWaypoints(coordinates, {
            routeStops: routeIndex !== null ? this.getRouteStops(routeIndex) : [],
            stopMinutes: window.travelPreferences?.fuelStopDurationMinutes || 30,
            pinnedWindPoint,
            hotspots
        });
    }

    /**
     * Fetch wind hotspots along a route corridor from the backend.
     * Returns hotspots within the route's bounding box.
     * Graceful fallback — returns empty array on any error so normal sampling continues.
     */
    async fetchWindHotspots(coordinates) {
        // Hotspots are US data only (WYDOT/CDOT corridors).
        if (!hasUsOnlyData()) return [];
        return fetchWindHotspots(coordinates, import.meta.env.VITE_BACKEND_URL);
    }

    /**
     * Calculate distance between two points in miles
     */
    calculateDistance(lat1, lon1, lat2, lon2) {
        const R = 3959; // Earth's radius in miles
        const dLat = this.toRad(lat2 - lat1);
        const dLon = this.toRad(lon2 - lon1);
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    }

    toRad(degrees) {
        return degrees * (Math.PI / 180);
    }

    async handleCalculateRoute() {
        console.log('Calculate route button clicked');
        try {
            const startEl = document.getElementById('start');

            // A route restored after a sign-in interruption can show a
            // "Current Location" label whose precise coordinate never
            // survived the redirect (see saveRouteForAfterSignIn /
            // restorePendingRoute in main.js) -- re-resolve live GPS here
            // instead of letting RouteService fall back to geocoding that
            // stale text, which is what sent routes to wherever GPS was
            // last resolved (confirmed recurring at Idaho Springs) instead
            // of the user's actual current position.
            if (startEl.dataset.needsGpsRefresh === 'true' && window.resolveCurrentLocationIntoStart) {
                try {
                    await window.resolveCurrentLocationIntoStart();
                } catch (e) {
                    alert('Could not refresh your current location. Please tap the 📍 button and try again.');
                    return;
                }
            }

            const start = startEl.value;
            const destination = document.getElementById('destination').value;
            const departureDate = document.getElementById('departureDate').value;
            const departureTime = document.getElementById('departureTime').value;

            console.log('Start:', start, 'Destination:', destination);
            console.log('Departure:', departureDate, departureTime);

            if (!start || !destination) {
                alert('Please enter both start and destination locations');
                return;
            }
            
            if (!departureDate || !departureTime) {
                alert('Please select departure date and time');
                return;
            }

            // Combine date and time into ISO string
            const departureDateTime = new Date(`${departureDate}T${departureTime}`);
            console.log('Departure DateTime:', departureDateTime.toISOString());

            // Require sign in to use the app
            if (!window.authService || !window.authService.isSignedIn()) {
                // Save route inputs before showing the prompt so the form
                // survives whichever auth path the user picks -- restored
                // by restorePendingRoute() + the auto-Calculate block in
                // main.js once they're back signed in.
                if (window.saveRouteForAfterSignIn) window.saveRouteForAfterSignIn();
                showSignInPrompt();
                return;
            }

            // Load route status to show counter (for free users)
            if (!window.isSubscribed) {
                const user = window.authService.getUser();
                if (user && !routeTrackingService.launchStatus) {
                    await routeTrackingService.getRouteStatus(user.sub);
                    this.updateRouteCounter();
                }
            }

            // Get RV specifications
            const rvSpecs = {
                type: document.getElementById('rvType').value,
                height: parseFloat(document.getElementById('height').value),
                weight: parseFloat(document.getElementById('weight').value),
                length: parseFloat(document.getElementById('length').value)
            };
            
            // Check if this is a truck-only profile
            const isTruckOnly = rvSpecs.type === 'truck-only';
            if (isTruckOnly) {
                rvSpecs.truckOnly = true;
            }
            
            // Get tow vehicle details if applicable (for trailers or truck-only)
            const isTrailer = ['fifth-wheel', 'travel-trailer'].includes(rvSpecs.type);
            if (isTrailer || isTruckOnly) {
                rvSpecs.towVehicle = {
                    type: document.getElementById('towVehicleType').value || null,
                    engine: document.getElementById('towVehicleEngine').value || null,
                    capacity: parseFloat(document.getElementById('towCapacity').value) || null,
                    weight: parseFloat(document.getElementById('towVehicleWeight').value) || null,
                    hasSwayControl: document.getElementById('hasSwayControl').checked,
                    hasTrailerBrakes: document.getElementById('hasTrailerBrakes').checked
                };
            }

            // Get display options
            const showWindIcons = document.getElementById('showWindIcons').checked;

            // Show loading state
            this.showLoading();
            
            // Clear previous gas station markers (and drop any search still running)
            this.gasStationRequest = (this.gasStationRequest || 0) + 1;
            this.mapService.clearGasStationMarkers();
            
            // Clear stale stops from previous calculation — prevents shifted arrival times
            // affecting wind forecast queries on recalculate. Stops survive only a
            // recalculation made to route through them (recalculateThroughStops).
            const carriedStops = this._carryStops || [];
            this._carryStops = null;
            this.routeStopsMap = {};
            this.routeService.stopsForNextRoute = carriedStops;

            // Calculate multiple routes
            const routeData = await this.routeService.calculateRoute(start, destination, rvSpecs);
            const routes = routeData.routes;
            if (carriedStops.length > 0 && routes?.length) {
                // One route back (alternatives off when routing through stops).
                this.routeStopsMap[0] = carriedStops.map(stop => ({
                    ...stop,
                    distanceAlongRoute: this.calculateDistanceAlongRoute(stop.lat, stop.lng, routes[0].coordinates)
                })).sort((x, y) => x.distanceAlongRoute - y.distanceAlongRoute);
            }

            console.log(`Found ${routes.length} route(s)`);

            // Put the routes on the map now (~4s) instead of after wind and
            // elevation (~10s); the rest fills in below when ready.
            this.showRoutePreview(routeData);
            const avoidLowClearanceChecked = document.getElementById('avoidLowClearance')?.checked;
            if (window.routeAnalysisPending && window.startRouteProgress) {
                window.startRouteProgress({
                    wind: showWindIcons ? 'pending' : 'skip',
                    elevation: 'pending',
                    clearances: avoidLowClearanceChecked && this.clearanceService ? 'pending' : 'skip',
                    fuel: document.getElementById('showGasStations')?.checked ? 'pending' : 'skip'
                });
            }
            // Wind and elevation tick off once every route option has them.
            let windDoneCount = 0;
            let elevationDoneCount = 0;
            const tick = (step) => window.setRouteProgress && window.setRouteProgress(step, 'done');

            // Analyze each route for wind and elevation IN PARALLEL
            const analyzedRoutes = await Promise.all(routes.map(async (route, i) => {
                let windAnalysis = null;
                let elevationAnalysis = null;
                let windData = null;
                let timeAwareWindProfile = null;
                let windHotspots = [];

                // Get wind data if enabled
                if (showWindIcons) {
                    console.log(`Fetching time-aware wind data for route ${i + 1}...`);
                    
                    // Fetch wind hotspots for this route's corridor (graceful fallback to empty)
                    const hotspots = await this.fetchWindHotspots(route.coordinates);
                    
                    // Prepare waypoints with distance information (includes stops)
                    const waypoints = this.prepareWaypointsForWindForecast(route.coordinates, i, null, hotspots);
                    
                    // Build time-aware wind profile
                    timeAwareWindProfile = await this.windService.buildRouteWindProfile(
                        departureDateTime,
                        waypoints,
                        rvSpecs,
                        window.travelPreferences || {}
                    );
                    
                    windData = timeAwareWindProfile.windProfile.map(wp => wp.wind);
                    windAnalysis = this.buildWindAnalysis(timeAwareWindProfile);
                    windHotspots = hotspots;
                }

                if (showWindIcons && ++windDoneCount === routes.length) tick('wind');

                // Get elevation data (always fetch for route scoring)
                console.log(`Fetching elevation data for route ${i + 1}...`);
                // Use many more samples for accurate grade detection (200-300 points)
                const sampleCount = Math.min(300, Math.max(200, Math.floor(route.coordinates.length / 5)));
                elevationAnalysis = await this.elevationService.getElevationAlongPath(route.coordinates, sampleCount);
                if (++elevationDoneCount === routes.length) tick('elevation');

                return {
                    ...route,
                    windAnalysis,
                    elevationAnalysis,
                    windData,
                    timeAwareWindProfile,
                    // Kept so the departure slider samples the same places.
                    windHotspots,
                    // Phase 19: Pin the worst wind coordinate so it's never lost on recalculate
                    pinnedWindPoint: timeAwareWindProfile?.worstPoint ? {
                        lat: timeAwareWindProfile.worstPoint.lat,
                        lng: timeAwareWindProfile.worstPoint.lng,
                        originalSpeed: timeAwareWindProfile.worstPoint.wind?.speed || 0,
                        originalGust: timeAwareWindProfile.worstPoint.wind?.gust || 0,
                        originalArrivalTime: timeAwareWindProfile.worstPoint.arrivalTime
                    } : null,
                    routeIndex: i
                };
            }));

            console.log('All routes analyzed:', analyzedRoutes);
            
            // Debug: Log wind data for each route
            analyzedRoutes.forEach((route, i) => {
                console.log(`Route ${i + 1}:`, {
                    hasWindData: !!route.windData,
                    hasWindAnalysis: !!route.windAnalysis,
                    hasElevationAnalysis: !!route.elevationAnalysis,
                    avgWind: route.windAnalysis?.avgWindSpeed,
                    maxWind: route.windAnalysis?.maxWindSpeed,
                    timeAware: route.windAnalysis?.timeAware
                });
            });

            // Determine best route
            const bestRoute = this.selectBestRoute(analyzedRoutes, rvSpecs);
            this.lastRvSpecs = rvSpecs;
            this.currentRoutes = analyzedRoutes;
            this.bestRouteIndex = bestRoute.index;

            console.log(`Best route is Route ${bestRoute.index + 1}`);

            // Clear map
            this.mapService.clearRoute();
            this.mapService.clearMarkers();

            // Draw all routes with different colors
            this.mapService.drawMultipleRoutes(analyzedRoutes, bestRoute.index, (index) => {
                this.selectRoute(index);
            });

            // Add start and end markers
            const firstRoute = analyzedRoutes[0];
            this.mapService.addMarker(
                firstRoute.coordinates[0].lat,
                firstRoute.coordinates[0].lng,
                'Start: ' + routeData.startAddress,
                'green'
            );
            
            // Calculate total route distance for destination marker
            let totalDistance = 0;
            for (let i = 0; i < firstRoute.coordinates.length - 1; i++) {
                const lat1 = firstRoute.coordinates[i].lat;
                const lng1 = firstRoute.coordinates[i].lng;
                const lat2 = firstRoute.coordinates[i + 1].lat;
                const lng2 = firstRoute.coordinates[i + 1].lng;
                
                // Haversine distance
                const R = 3959; // Earth's radius in miles
                const dLat = (lat2 - lat1) * Math.PI / 180;
                const dLng = (lng2 - lng1) * Math.PI / 180;
                const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                          Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                          Math.sin(dLng / 2) * Math.sin(dLng / 2);
                const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
                totalDistance += R * c;
            }
            
            const destLabel = `<strong>Destination</strong><br>${routeData.endAddress}<br><br><strong>📍 ${units.distanceText(totalDistance, { decimals: 1, long: true })}</strong> from start`;
            
            this.mapService.addMarker(
                firstRoute.coordinates[firstRoute.coordinates.length - 1].lat,
                firstRoute.coordinates[firstRoute.coordinates.length - 1].lng,
                destLabel,
                'red'
            );

            // Display route comparison
            this.displayRouteComparison(analyzedRoutes, bestRoute);

            // Immediate pop-up if the RECOMMENDED route is one AWS itself
            // flagged as violating a height/weight/width restriction --
            // don't rely on the driver noticing it buried in a route card.
            // Alternate routes flagged the same way are visible in their own
            // cards without interrupting with a second pop-up.
            const bestRouteViolations = (bestRoute.route.restrictionWarnings || [])
                .filter(w => w.code === 'ViolatedVehicleRestriction');
            if (bestRouteViolations.length > 0 && window.showToast) {
                window.showToast(
                    `⚠️ Route ${bestRoute.index + 1} may violate a vehicle restriction (height/weight/width) -- check the route details before driving`,
                    { type: 'warning' }
                );
            }

            // Display details for best route
            // Keep wind markers on map but hide the detailed sections by default
            if (showWindIcons && bestRoute.route.timeAwareWindProfile) {
                // Pass the full wind profile with arrival times
                this.mapService.addWindMarkers(bestRoute.route.timeAwareWindProfile.windProfile);
            }
            
            // Hide elevation and wind detail sections
            document.getElementById('elevationChart').classList.add('hidden');
            document.getElementById('windOverlay').classList.add('hidden');

            // Show gas stations if enabled (non-blocking)
            const showGasStations = document.getElementById('showGasStations').checked;
            console.log('Show gas stations checkbox:', showGasStations);
            
            if (showGasStations) {
                console.log('🔍 Starting gas station search...');
                
                // Show loading indicator for gas stations
                const routeInfo = document.getElementById('routeInfo');
                const gasLoadingMsg = document.createElement('div');
                gasLoadingMsg.id = 'gasLoadingMsg';
                gasLoadingMsg.style.cssText = 'padding: 0.5rem; background: #e3f2fd; border-radius: 4px; margin-top: 0.5rem; font-size: 0.85rem; color: #1976d2;';
                gasLoadingMsg.textContent = w('⛽ Loading gas stations...');
                routeInfo.appendChild(gasLoadingMsg);
                
                // Run gas station fetch in background, don't wait for it
                this.displayGasStations(bestRoute.route).then(() => {
                    console.log('✓ Gas station search completed');
                    if (window.setRouteProgress) window.setRouteProgress('fuel', 'done');
                    const msg = document.getElementById('gasLoadingMsg');
                    if (msg) {
                        msg.textContent = w('✓ Gas stations loaded');
                        msg.style.background = '#e8f5e9';
                        msg.style.color = '#2e7d32';
                        setTimeout(() => msg.remove(), 3000);
                    }
                }).catch(err => {
                    console.error('❌ Gas station fetch failed (non-critical):', err);
                    if (window.setRouteProgress) window.setRouteProgress('fuel', 'failed');
                    const msg = document.getElementById('gasLoadingMsg');
                    if (msg) {
                        msg.textContent = w('⚠️ Gas stations unavailable (slow connection)');
                        msg.style.background = '#fff3e0';
                        msg.style.color = '#e65100';
                        setTimeout(() => msg.remove(), 5000);
                    }
                });
            } else {
                console.log('Gas stations disabled by user');
                this.gasStationRequest = (this.gasStationRequest || 0) + 1;
                this.mapService.clearGasStationMarkers();
            }

            this.hideLoading();

            // Phase 17: Check low clearances if enabled (non-blocking)
            const avoidLowClearance = document.getElementById('avoidLowClearance')?.checked;
            if (avoidLowClearance && this.clearanceService) {
                const rigHeight = parseFloat(document.getElementById('height').value) || 12;
                
                // Check all routes for clearance issues
                Promise.all(analyzedRoutes.map(route => 
                    this.clearanceService.checkClearances(route.coordinates, rigHeight, {
                        steps: route.allSteps || route.legs?.[0]?.steps
                    })
                )).then(allWarnings => {
 window._routeClearanceWarnings = allWarnings;
                    this.clearanceWarningsFor = analyzedRoutes;
                    if (window.setRouteProgress) window.setRouteProgress('clearances', allWarnings.some(w => w.unavailable) ? 'failed' : 'done');
                    if (allWarnings.some(w => w.unavailable) && window.showToast) {
                        window.showToast("Couldn't check low clearances on this route — watch for height signs.", { type: 'warning' });
                    }
                    // Find which routes have danger-level clearances
                    const routeDangers = allWarnings.map(warnings => 
                        warnings.filter(w => w.severity === 'danger').length
                    );
                    
                    const currentBestIdx = this.bestRouteIndex;
                    const currentBestDangers = routeDangers[currentBestIdx];
                    
                    // If best route has dangers, try to find one without
                    if (currentBestDangers > 0) {
                        const saferIdx = routeDangers.findIndex(d => d === 0);
                        if (saferIdx !== -1 && saferIdx !== currentBestIdx) {
                            console.log(`🚧 Switching best route from ${currentBestIdx + 1} to ${saferIdx + 1} (clearance issues)`);
                            this.bestRouteIndex = saferIdx;
                            // Redraw routes with new best highlighted
                            this.mapService.clearRoute();
                            const colors = ['#1e88e5', '#48bb78', '#ed8936', '#e53e3e'];
                            analyzedRoutes.forEach((route, index) => {
                                if (index !== saferIdx) {
                                    this.mapService.drawRoute(route.coordinates, { color: colors[index % colors.length], weight: 4, opacity: 0.5 });
                                }
                            });
                            this.mapService.drawRoute(analyzedRoutes[saferIdx].coordinates, { color: colors[saferIdx % colors.length], weight: 8, opacity: 1.0 });
                        }
                    }
                    
                    // Display warnings for the best route
                    const bestWarnings = allWarnings[this.bestRouteIndex];
                    if (bestWarnings.length > 0) {
                        this.displayClearanceWarnings(bestWarnings);
                    }
                }).catch(err => {
                    console.warn('Clearance check failed (non-blocking):', err);
                    if (window.setRouteProgress) window.setRouteProgress('clearances', 'failed');
                });
            }

            // Phase 18: Check road quality along route (non-blocking). US data only.
            if (this.roadQualityService && hasUsOnlyData()) {
                if (window.showToast) window.showToast('Loading road conditions...');
                const bestRoute = analyzedRoutes[this.bestRouteIndex];
                const userPlan = window.routeTrackingService?.launchStatus?.plan
                    || (window.routeTrackingService?.isPremium() ? 'premium' : null)
                    || (window.isSubscribed ? 'premium' : 'free');
                this.roadQualityService.getRoadQualityForRoute(bestRoute.coordinates, userPlan, bestRoute.highways)
                    .then(roadQuality => {
                        // Store first so updateRouteCard can read it
                        this.currentRoadQuality = roadQuality;
                        this.displayRoadQualityWarnings(roadQuality);
                    })
                    .catch(err => console.warn('Road quality check failed (non-blocking):', err));
            }

            // Phase 21: Fetch campgrounds near route (non-blocking).
            if (this.campgroundService) {
                if (window.showToast) window.showToast('Loading campgrounds...');
                const bestRoute = analyzedRoutes[this.bestRouteIndex];
                const userPlan = window.routeTrackingService?.launchStatus?.plan
                    || (window.isSubscribed ? 'premium' : 'free');
                this.campgroundService.getCampgroundsNearRoute(bestRoute.coordinates, userPlan)
                    .then(result => {
                        this.currentCampgrounds = result.campgrounds || [];
                        if (window.applyCampgroundFilters) {
                            window.applyCampgroundFilters();
                        } else {
                            this.displayCampgroundMarkers(this.currentCampgrounds);
                        }
                    })
                    .catch(err => console.warn('Campgrounds fetch failed (non-blocking):', err));
            }

            // Road hazards other travellers reported near this route (non-blocking).
            this.loadRoadReports(analyzedRoutes[this.bestRouteIndex].coordinates);

            const previewShown = window.routeAnalysisPending;
            window.routeAnalysisPending = false;
            if (previewShown) {
                // The map and card are already up from the preview: refresh the
                // card in place (keeps it open if the traveller expanded it) and
                // re-fit to the route Zephyr picked.
                if (window.updateRouteCard) window.updateRouteCard();
                if (window.updateCardLaunchButton) window.updateCardLaunchButton();
                if (this.mapService.fitRouteToView) this.mapService.fitRouteToView(bestRoute.route.coordinates);
            } else {
                // Mobile: switch to fullscreen map after route calculation
                if (window.showMobileMapToggle) window.showMobileMapToggle();

                // Phase 15: On desktop, also show the floating route card on the map
                if (window.innerWidth > 1024 && window.showRouteCard) {
                    window.showRouteCard();
                }
            }

        } catch (error) {
            console.error('Error calculating route:', error);
            if (window.clearRouteProgress) window.clearRouteProgress();
            if (window.routeAnalysisPending) {
                window.routeAnalysisPending = false;
                if (window.updateRouteCard) window.updateRouteCard();
                if (window.updateCardLaunchButton) window.updateCardLaunchButton();
            }
            alert('Error calculating route. Please try again.');
            this.hideLoading();
        }
    }

    /**
     * Display gas stations along route
     */
    async displayGasStations(route) {
        console.log('📍 displayGasStations called');
        console.log('Route has', route.coordinates?.length || 0, 'coordinates');
        // Only the newest search may draw: turning fuel on and then picking
        // Love's starts two searches, and the slower "all stations" one used
        // to land last and replace the Love's markers.
        const request = this.gasStationRequest = (this.gasStationRequest || 0) + 1;
        const show = (stations) => {
            if (request !== this.gasStationRequest) return false;
            // None found (e.g. no Love's on this route): show none, not the
            // previous filter's markers.
            if (stations.length > 0) this.mapService.addGasStationMarkers(stations);
            else this.mapService.clearGasStationMarkers();
            return true;
        };

        try {
            // Phase 16: Read multi-select checkboxes
            const checked = Array.from(document.querySelectorAll('.fuel-chain-check:checked'))
                .map(cb => cb.value);
            console.log('Filter selected:', checked);
            
            // If "all" is checked or nothing is checked, show all stations
            if (checked.includes('all') || checked.length === 0) {
                let options = { maxDistance: 5.0, sampleInterval: 30 };
                console.log('🔍 Fetching all gas stations with options:', options);
                const stations = await this.gasStationService.findGasStationsAlongRoute(
                    route.coordinates, options
                );
                console.log('📊 Received', stations.length, 'stations from service');
                if (show(stations)) console.log(`✅ Displayed ${stations.length} gas stations on map`);
                return;
            }

            // Collect results from all selected filters
            let allStations = [];

            // RV-friendly filter (OSM-based)
            if (checked.includes('rv-friendly')) {
                const rvStations = await this.gasStationService.findGasStationsAlongRoute(
                    route.coordinates,
                    { rvFriendly: true, maxDistance: 0.5, sampleInterval: 30 }
                );
                allStations.push(...rvStations);
            }

            // Brand filters — combine into pipe-separated string for curated DB
            const brandMap = { loves: 'loves', pilot: 'pilot|flyingj', ta: 'ta|petro' };
            const selectedBrands = checked
                .filter(v => brandMap[v])
                .map(v => brandMap[v])
                .join('|');

            // Australian chains: all stations along the route, kept by brand/name
            const auChains = checked.filter(v => v.startsWith('au:')).map(v => v.slice(3));
            if (auChains.length) {
                const stations = await this.gasStationService.findGasStationsAlongRoute(
                    route.coordinates, { auChains, maxDistance: 5.0, sampleInterval: 30 }
                );
                allStations.push(...stations);
            }

            if (selectedBrands) {
                const brandStations = await this.gasStationService.findGasStationsAlongRoute(
                    route.coordinates,
                    { brand: selectedBrands, maxDistance: 5.0, sampleInterval: 30 }
                );
                allStations.push(...brandStations);
            }

            // Deduplicate by lat/lng
            const seen = new Set();
            const unique = allStations.filter(s => {
                const key = `${s.lat.toFixed(4)},${s.lng.toFixed(4)}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });

            console.log(`📊 Combined ${unique.length} unique stations from ${checked.length} filter(s)`);
            if (show(unique)) console.log(`✅ Displayed ${unique.length} gas stations on map`);
        } catch (error) {
            console.error('❌ Error displaying gas stations:', error);
            throw error;
        }
    }

    /**
     * Select the best route based on wind, elevation, distance, and RV type
     */
    selectBestRoute(routes, rvSpecs) {
        // Import RVProfileService at the top of the method
        const RVProfileService = window.RVProfileService;
        
        let bestScore = -Infinity;
        let bestIndex = 0;
        let bestReasons = [];

        routes.forEach((route, index) => {
            let score = 100; // Start with base score
            const reasons = [];

            // Prepare route stats for RVProfileService
            const routeStats = {
                distance: route.distanceValue,
                avgWindSpeed: route.windAnalysis?.avgWindSpeed || 0,
                maxWindSpeed: route.windAnalysis?.maxWindSpeed || 0,
                maxGrade: route.elevationAnalysis?.stats.maxGrade || 0,
                steepGradeCount: route.elevationAnalysis?.stats.steepGradeCount || 0,
                maxElevation: route.elevationAnalysis?.stats.maxElevation || 0,
                totalGain: route.elevationAnalysis?.stats.totalGain || 0,
                totalLoss: route.elevationAnalysis?.stats.totalLoss || 0
            };

            // Get RV-type-specific adjusted penalties
            const adjustedPenalties = RVProfileService.getAdjustedPenalties(
                rvSpecs.type,
                rvSpecs,
                routeStats
            );

            // Apply all penalties
            score -= adjustedPenalties.penalties.distance;
            score -= adjustedPenalties.penalties.wind;
            score -= adjustedPenalties.penalties.grade;
            score -= adjustedPenalties.penalties.elevation;

            // Also apply total elevation change penalty
            if (route.elevationAnalysis) {
                const totalElevationChange = routeStats.totalGain + routeStats.totalLoss;
                const elevationChangePenalty = Math.min(totalElevationChange / 500, 40);
                score -= elevationChangePenalty;
            }

            // Generate reasons based on route characteristics
            const isTruckOnly = rvSpecs.type === 'truck-only' || rvSpecs.truckOnly;
            const vehicleType = isTruckOnly ? 'truck' : 'RV';
            
            console.log(`Route ${index + 1} - Vehicle type: ${rvSpecs.type}, isTruckOnly: ${isTruckOnly}, truckOnly flag: ${rvSpecs.truckOnly}`);
            
            // Wind reasons
            if (route.windAnalysis) {
                if (route.windAnalysis.avgWindSpeed < 10) {
                    reasons.push(isTruckOnly ? 'Low wind speeds (comfortable driving)' : 'Low wind speeds (safer for RVs)');
                } else if (route.windAnalysis.avgWindSpeed > 20) {
                    reasons.push('⚠️ High wind speeds (use caution)');
                }
                
                if (route.windAnalysis.warnings.length === 0) {
                    reasons.push('No wind warnings along route');
                }
                
                // Tow vehicle specific
                if (rvSpecs.towVehicle && rvSpecs.towVehicle.hasSwayControl && !isTruckOnly) {
                    reasons.push('✓ Sway control reduces wind risk');
                }
            }

            // Elevation reasons
            if (route.elevationAnalysis) {
                const stats = route.elevationAnalysis.stats;
                const totalElevationChange = stats.totalGain + stats.totalLoss;
                
                // Max grade warnings - adjusted for truck-only
                if (isTruckOnly) {
                    // Trucks can handle steeper grades
                    if (stats.maxGrade > 10) {
                        reasons.push('⚠️ Very steep grades (max ' + stats.maxGrade + '%)');
                    } else if (stats.maxGrade > 7) {
                        reasons.push('Steep sections (max ' + stats.maxGrade + '% - manageable for truck)');
                    }
                } else {
                    // RV grade warnings
                    if (stats.maxGrade > 8) {
                        reasons.push('⚠️ DANGEROUS: Very steep grades (max ' + stats.maxGrade + '%)');
                    } else if (stats.maxGrade > 6) {
                        reasons.push('⚠️ Very steep grades (max ' + stats.maxGrade + '%)');
                    } else if (stats.maxGrade > 4) {
                        reasons.push('⚠️ Steep sections (max ' + stats.maxGrade + '%)');
                    }
                }

                // Max elevation warnings
                if (stats.maxElevation > 11000) {
                    reasons.push('⚠️ EXTREME elevation (max ' + units.elevationText(stats.maxElevation) + ' - very thin air)');
                } else if (stats.maxElevation > 10000) {
                    reasons.push('⚠️ Very high elevation (max ' + units.elevationText(stats.maxElevation) + ' - thin air)');
                } else if (stats.maxElevation > 9000) {
                    reasons.push('⚠️ High elevation (max ' + units.elevationText(stats.maxElevation) + ')');
                } else if (stats.maxElevation > 8000) {
                    reasons.push('Moderate elevation (max ' + units.elevationText(stats.maxElevation) + ')');
                }

                // Steep grade count - adjusted for truck-only
                if (isTruckOnly) {
                    if (stats.steepGradeCount === 0 && stats.maxGrade < 5) {
                        reasons.push('Gentle grades throughout');
                    } else if (stats.steepGradeCount > 15) {
                        reasons.push('Many steep grades (' + stats.steepGradeCount + ' sections over 4%)');
                    }
                } else {
                    if (stats.steepGradeCount === 0 && stats.maxGrade < 3) {
                        reasons.push('Gentle grades throughout (easy on engine)');
                    } else if (stats.steepGradeCount === 0) {
                        reasons.push('No steep grades (max ' + stats.maxGrade + '%)');
                    } else if (stats.steepGradeCount > 10) {
                        reasons.push('⚠️ Many steep grades (' + stats.steepGradeCount + ' sections over 4%)');
                    } else if (stats.steepGradeCount > 0) {
                        reasons.push('Contains ' + stats.steepGradeCount + ' steep grade(s) over 4%');
                    }
                }
                
                if (totalElevationChange < 2000) {
                    reasons.push('Minimal elevation changes');
                }
                
                // Tow vehicle specific (only for RVs with trailers)
                if (rvSpecs.towVehicle && !isTruckOnly) {
                    if (rvSpecs.towVehicle.engine === 'diesel') {
                        reasons.push('✓ Diesel engine handles altitude well');
                    }
                    if (rvSpecs.towVehicle.hasTrailerBrakes) {
                        reasons.push('✓ Trailer brakes help on descents');
                    }
                    
                    // Capacity warnings
                    if (rvSpecs.towVehicle.capacity) {
                        const capacityRatio = rvSpecs.weight / rvSpecs.towVehicle.capacity;
                        if (capacityRatio > 0.9) {
                            reasons.push('⚠️ Towing at maximum capacity - use caution');
                        } else if (capacityRatio < 0.6) {
                            reasons.push('✓ Plenty of towing capacity margin');
                        }
                    }
                }
            }

            // RV type specific reasons (skip for truck-only)
            if (!isTruckOnly) {
                const profile = adjustedPenalties.profile;
                if (profile.windSensitivity > 1.2 && routeStats.avgWindSpeed > 15) {
                    reasons.push('⚠️ ' + profile.name + ' is very wind-sensitive');
                }
                if (profile.gradeSensitivity > 1.2 && routeStats.maxGrade > 6) {
                    reasons.push('⚠️ ' + profile.name + ' may struggle on steep grades');
                }
            }

            console.log(`Route ${index + 1} score: ${Math.round(score)}, steep grades: ${routeStats.steepGradeCount}, max grade: ${routeStats.maxGrade}%, max elevation: ${routeStats.maxElevation} ft, total elev change: ${routeStats.totalGain + routeStats.totalLoss} ft, RV type: ${rvSpecs.type}, tow multiplier: ${adjustedPenalties.towVehicleMultiplier?.toFixed(2) || 'N/A'}`);

            if (score > bestScore) {
                bestScore = score;
                bestIndex = index;
                bestReasons = reasons;
            }
        });

        return {
            index: bestIndex,
            route: routes[bestIndex],
            score: bestScore,
            reasons: bestReasons.length > 0 ? bestReasons : ['Balanced route option']
        };
    }

    /**
     * Turn one AWS restrictionWarnings entry into a human-readable line.
     * violatedConstraints values are in centimeters/kilograms (AWS's units
     * for this API) -- converted to ft/lbs since that's what the rest of
     * the app's UI already uses.
     */
    describeRestrictionWarning(w) {
        const vc = w.details?.[0]?.violatedConstraints;
        const parts = [];
        if (vc?.maxHeightCm) parts.push(units.isMetric() ? `max height ${(Math.floor(vc.maxHeightCm / 10) / 10).toFixed(1)} m` : `max height ${(vc.maxHeightCm / 30.48).toFixed(1)}ft`);
        if (vc?.maxWidthCm) parts.push(units.isMetric() ? `max width ${(Math.floor(vc.maxWidthCm / 10) / 10).toFixed(1)} m` : `max width ${(vc.maxWidthCm / 30.48).toFixed(1)}ft`);
        if (vc?.maxLengthCm) parts.push(units.isMetric() ? `max length ${(Math.floor(vc.maxLengthCm / 10) / 10).toFixed(1)} m` : `max length ${(vc.maxLengthCm / 30.48).toFixed(1)}ft`);
        if (vc?.maxWeightKg) parts.push(units.isMetric() ? `max weight ${(Math.floor(vc.maxWeightKg / 100) / 10).toFixed(1)} t` : `max weight ${Math.round(vc.maxWeightKg * 2.20462).toLocaleString()}lbs`);
        const label = w.code === 'ViolatedVehicleRestriction'
            ? 'Restriction violated'
            : 'Possible restriction';
        return parts.length > 0 ? `${label}: ${parts.join(', ')}` : `${label} on this route`;
    }

    /**
     * Display comparison of all routes
     */
    displayRouteComparison(routes, bestRoute) {
        const routeInfo = document.getElementById('routeInfo');
        const routeStats = document.getElementById('routeStats');

        const colors = ['#1e88e5', '#48bb78', '#ed8936', '#e53e3e'];
        
        let html = '<h3>Route Options</h3>';
        
        routes.forEach((route, index) => {
            const isBest = index === bestRoute.index;
            const color = colors[index % colors.length];
            
            // Determine wind risk level
            let windRiskColor = '#4CAF50';
            let windRiskText = 'Low';
            let hasWindData = false;
            
            if (route.windAnalysis) {
                hasWindData = true;
                if (route.windAnalysis.avgWindSpeed > 20) {
                    windRiskColor = '#F44336';
                    windRiskText = 'High';
                } else if (route.windAnalysis.avgWindSpeed > 15) {
                    windRiskColor = '#FFC107';
                    windRiskText = 'Moderate';
                }
            }

            // Determine elevation difficulty
            let elevationDifficulty = 'Easy';
            let elevationColor = '#4CAF50';
            let hasElevationData = false;
            
            if (route.elevationAnalysis) {
                hasElevationData = true;
                const stats = route.elevationAnalysis.stats;
                const routeMiles = route.distanceValue || 0;
                
                // If maxGrade is 0 on a route > 20 miles, data is unreliable
                if (stats.maxGrade === 0 && routeMiles > 20) {
                    hasElevationData = false; // Will show "not available"
                } else {
                    const totalChange = stats.totalGain + stats.totalLoss;
                    
                    // Consider both total change and steep grades
                    // For RVs: >4% is challenging, >6% is very challenging
                    if (stats.steepGradeCount > 5 || stats.maxGrade > 6 || totalChange > 5000) {
                        elevationDifficulty = 'Challenging';
                        elevationColor = '#F44336';
                    } else if (stats.steepGradeCount > 0 || stats.maxGrade > 4 || totalChange > 2000) {
                        elevationDifficulty = 'Moderate';
                        elevationColor = '#FFC107';
                    }
                }
            }
            
            html += `
                <div style="
                    margin-bottom: 1rem; 
                    padding: 1.25rem; 
                    background: ${isBest ? 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)' : '#f8f9fa'}; 
                    border: ${isBest ? '3px solid #1e88e5' : '2px solid #e0e0e0'};
                    border-radius: 8px;
                    ${isBest ? 'box-shadow: 0 4px 12px rgba(30, 136, 229, 0.3);' : ''}
                    cursor: pointer;
                    transition: all 0.2s;
                " 
                onmouseover="this.style.transform='translateX(4px)'"
                onmouseout="this.style.transform='translateX(0)'"
                onclick="window.selectRoute(${index})">
                    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.75rem;">
                        <div>
                            <div style="display: flex; align-items: center; gap: 0.5rem;">
                                <div style="width: 20px; height: 20px; background: ${color}; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.2);"></div>
                                <strong style="color: ${color}; font-size: 1.2rem;">Route ${index + 1}</strong>
                            </div>
                            ${isBest ? `
                                <div style="
                                    display: inline-block;
                                    background: linear-gradient(135deg, #1e88e5 0%, #1565c0 100%);
                                    color: white;
                                    padding: 0.35rem 0.75rem;
                                    border-radius: 20px;
                                    font-size: 0.8rem;
                                    font-weight: 600;
                                    margin-top: 0.5rem;
                                    box-shadow: 0 2px 6px rgba(102, 126, 234, 0.4);
                                ">
                                    ★ BEST FOR YOUR RV
                                </div>
                            ` : ''}
                        </div>
                        ${!isBest ? '<span style="font-size: 0.85rem; color: #666;">Click to view details</span>' : ''}
                    </div>
                    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem; font-size: 0.95rem; margin-bottom: 0.75rem;">
                        <div><strong>Distance:</strong> ${units.routeDistanceText(route)}</div>
                        <div><strong>Duration:</strong> ${route.duration}</div>
                    </div>
                    ${hasWindData ? `
                        <div style="background: white; padding: 0.75rem; border-radius: 6px; margin-bottom: 0.5rem; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
                            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem;">
                                <strong style="font-size: 0.95rem;">🌬️ Wind Conditions</strong>
                                <span style="
                                    background: ${windRiskColor}; 
                                    color: white; 
                                    padding: 0.3rem 0.6rem; 
                                    border-radius: 4px; 
                                    font-size: 0.75rem;
                                    font-weight: 700;
                                    letter-spacing: 0.5px;
                                ">${windRiskText.toUpperCase()} RISK</span>
                            </div>
                            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem; font-size: 0.85rem;">
                                <div><strong>Average:</strong> ${Math.ceil(units.speed(route.windAnalysis.avgWindSpeed))} ${units.unit.speed()}</div>
                                <div><strong>Maximum:</strong> ${Math.ceil(units.speed(route.windAnalysis.maxWindSpeed))} ${units.unit.speed()}</div>
                            </div>
                            ${route.windAnalysis.warnings.length > 0 ? `
                                <div style="margin-top: 0.5rem; padding: 0.5rem; background: #fff3cd; border-radius: 4px; font-size: 0.8rem; color: #856404;">
                                    ⚠️ ${route.windAnalysis.warnings.length} wind alert(s) on this route
                                </div>
                            ` : `
                                <div style="margin-top: 0.5rem; padding: 0.5rem; background: #d4edda; border-radius: 4px; font-size: 0.8rem; color: #155724;">
                                    ✓ No wind warnings
                                </div>
                            `}
                        </div>
                    ` : '<div style="padding: 0.75rem; background: #ffebee; border-radius: 6px; text-align: center; color: #c62828; font-size: 0.85rem; margin-bottom: 0.5rem;">⚠️ Wind data not loaded for this route</div>'}
                    ${hasElevationData ? `
                        <div style="background: white; padding: 0.75rem; border-radius: 6px; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
                            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem;">
                                <strong style="font-size: 0.95rem;">⛰️ Elevation Profile</strong>
                                <span style="
                                    background: ${elevationColor}; 
                                    color: white; 
                                    padding: 0.3rem 0.6rem; 
                                    border-radius: 4px; 
                                    font-size: 0.75rem;
                                    font-weight: 700;
                                    letter-spacing: 0.5px;
                                ">${elevationDifficulty.toUpperCase()}</span>
                            </div>
                            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem; font-size: 0.85rem;">
                                <div><strong>Max Grade:</strong> ${route.elevationAnalysis.stats.maxGrade}%</div>
                                <div><strong>Steep Sections:</strong> ${route.elevationAnalysis.stats.steepGradeCount}</div>
                                <div><strong>Max Elevation:</strong> ${units.elevationText(route.elevationAnalysis.stats.maxElevation)}</div>
                                <div><strong>Min Elevation:</strong> ${units.elevationText(route.elevationAnalysis.stats.minElevation)}</div>
                            </div>
                            ${route.elevationAnalysis.stats.steepGradeCount > 0 ? `
                                <div style="margin-top: 0.5rem; padding: 0.5rem; background: #fff3cd; border-radius: 4px; font-size: 0.8rem; color: #856404;">
                                    ⚠️ ${route.elevationAnalysis.stats.steepGradeCount} steep grade(s) over 4%
                                </div>
                            ` : `
                                <div style="margin-top: 0.5rem; padding: 0.5rem; background: #d4edda; border-radius: 4px; font-size: 0.8rem; color: #155724;">
                                    ✓ No steep grades over 4% (RV-friendly)
                                </div>
                            `}
                        </div>
                    ` : '<div style="padding: 0.75rem; background: #f5f5f5; border-radius: 6px; text-align: center; color: #90a4ae; font-size: 0.85rem;">⛰️ Elevation data not available for this route</div>'}
                    ${route.restrictionWarnings && route.restrictionWarnings.length > 0 ? `
                        <div style="background: white; padding: 0.75rem; border-radius: 6px; margin-top: 0.5rem; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
                            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem;">
                                <strong style="font-size: 0.95rem;">🚧 Vehicle Restrictions</strong>
                                <span style="
                                    background: ${route.restrictionWarnings.some(w => w.code === 'ViolatedVehicleRestriction') ? '#F44336' : '#FFC107'};
                                    color: white;
                                    padding: 0.3rem 0.6rem;
                                    border-radius: 4px;
                                    font-size: 0.75rem;
                                    font-weight: 700;
                                    letter-spacing: 0.5px;
                                ">${route.restrictionWarnings.some(w => w.code === 'ViolatedVehicleRestriction') ? 'VIOLATION' : 'POSSIBLE ISSUE'}</span>
                            </div>
                            <div style="font-size: 0.8rem; color: #856404; background: #fff3cd; padding: 0.5rem; border-radius: 4px;">
                                ${route.restrictionWarnings.map(w => this.describeRestrictionWarning(w)).join('<br>')}
                            </div>
                        </div>
                    ` : `
                        <div style="margin-top: 0.5rem; padding: 0.5rem; background: #d4edda; border-radius: 4px; font-size: 0.8rem; color: #155724; text-align: center;">
                            ✓ No vehicle restriction violations detected on this route
                        </div>
                    `}
                    ${isBest ? `
                        <div style="margin-top: 0.75rem; padding: 0.75rem; background: rgba(30, 136, 229, 0.1); border-radius: 6px; border-left: 4px solid #1e88e5;">
                            <strong style="color: #1e88e5; font-size: 0.95rem;">✓ Why this route is recommended:</strong>
                            <ul style="margin: 0.5rem 0 0 1.25rem; font-size: 0.85rem; line-height: 1.6;">
                                ${bestRoute.reasons.map(reason => `<li>${reason}</li>`).join('')}
                            </ul>
                        </div>
                    ` : ''}
                    <div id="roadQualitySlot-${index}" style="margin-top: 0.5rem;"></div>
                </div>
            `;
        });

        routeStats.innerHTML = html;
        routeInfo.classList.remove('hidden');

        // Phase 27: sync the departure time slider to whatever the plan
        // form's departure time field actually holds
        if (window.initDepartureSlider) window.initDepartureSlider();
    }

    /**
     * Select and display a specific route
     */
    selectRoute(routeIndex, { userChoice = true } = {}) {
        console.log(`Selecting route ${routeIndex + 1}`);
        
        if (!this.currentRoutes || routeIndex >= this.currentRoutes.length) {
            console.error('Route not found:', routeIndex);
            return;
        }

        const route = this.currentRoutes[routeIndex];
        const rvSpecs = {
            height: parseFloat(document.getElementById('height').value)
        };

        // Update the selected route index. The departure slider switches to a
        // newly recommended route with userChoice false, so the route still
        // follows the recommendation until the user taps one.
        if (userChoice) this.selectedRouteIndex = routeIndex;

        // Redraw all routes with new highlighting
        this.redrawRoutes();

        // Keep elevation and wind sections hidden
        document.getElementById('elevationChart').classList.add('hidden');
        document.getElementById('windOverlay').classList.add('hidden');
        
        // Re-add wind markers for the selected route
        const showWindIcons = document.getElementById('showWindIcons').checked;
        if (showWindIcons && route.timeAwareWindProfile) {
            console.log('🌬️ Adding wind markers for Route', routeIndex + 1);
            this.mapService.addWindMarkers(route.timeAwareWindProfile.windProfile);
        } else {
            // Clear wind markers if wind is disabled
            this.mapService.windMarkers.forEach(marker => marker.remove());
            this.mapService.windMarkers = [];
        }
        
        // Update gas stations for the selected route
        const showGasStations = document.getElementById('showGasStations').checked;
        if (showGasStations) {
            console.log(`🔍 Updating gas stations for Route ${routeIndex + 1}`);
            
            // Get stops for this specific route
            const routeStops = this.getRouteStops(routeIndex);
            
            // If there are stops, use the last stop as the starting point
            let startCoords = route.coordinates;
            if (routeStops && routeStops.length > 0) {
                const lastStop = routeStops[routeStops.length - 1];
                
                // Find the index of the last stop in the route
                let startIndex = 0;
                let minDist = Infinity;
                
                route.coordinates.forEach((coord, index) => {
                    const dist = Math.sqrt(
                        Math.pow(coord.lat - lastStop.lat, 2) + 
                        Math.pow(coord.lng - lastStop.lng, 2)
                    );
                    if (dist < minDist) {
                        minDist = dist;
                        startIndex = index;
                    }
                });
                
                // Use remaining route from last stop
                startCoords = route.coordinates.slice(startIndex);
                console.log(`📍 Calculating from last stop, ${startCoords.length} points remaining`);
            }
            
            this.displayGasStations({ coordinates: startCoords }).then(() => {
                console.log('✓ Gas stations updated for selected route');
                
                // Re-add wind markers after gas stations are loaded
                const showWindIcons = document.getElementById('showWindIcons').checked;
                if (showWindIcons && route.timeAwareWindProfile) {
                    console.log('🌬️ Re-adding wind markers after gas station update');
                    this.mapService.addWindMarkers(route.timeAwareWindProfile.windProfile);
                }
            }).catch(err => {
                console.error('❌ Gas station fetch failed:', err);
            });
        }
        
        // Update trip summary for this specific route
        console.log('🔄 Updating trip summary for Route', routeIndex + 1);
        this.updateTripSummary();

        // Phase 15: Update the floating route card with new route data
        if (window.updateRouteCard) window.updateRouteCard();

        // Phase 21: Re-fetch campgrounds for the selected route
        if (this.campgroundService) {
            if (window.showToast) window.showToast('Loading campgrounds...');
            const userPlan = window.routeTrackingService?.launchStatus?.plan
                || (window.isSubscribed ? 'premium' : 'free');
            this.campgroundService.getCampgroundsNearRoute(route.coordinates, userPlan)
                .then(result => {
                    this.currentCampgrounds = result.campgrounds || [];
                    if (window.applyCampgroundFilters) {
                        window.applyCampgroundFilters();
                    } else {
                        this.displayCampgroundMarkers(this.currentCampgrounds);
                    }
                })
                .catch(err => console.warn('Campgrounds fetch on route switch failed:', err));
        }

        this.loadRoadReports(route.coordinates);

        console.log(`Route ${routeIndex + 1} selected successfully`);
    }

    /**
     * Redraw all routes with proper highlighting
     */
    redrawRoutes() {
        if (!this.currentRoutes) return;

        // Clear existing routes
        this.mapService.routeLayer.clearLayers();

        const colors = ['#1e88e5', '#48bb78', '#ed8936', '#e53e3e'];
        const selectedIndex = this.selectedRouteIndex !== undefined ? this.selectedRouteIndex : this.bestRouteIndex;

        // First pass: draw all non-selected routes (clickable)
        this.currentRoutes.forEach((route, index) => {
            if (index !== selectedIndex) {
                const polyline = L.polyline(route.coordinates, {
                    color: colors[index % colors.length],
                    weight: 5,
                    opacity: 0.5
                }).addTo(this.mapService.routeLayer);

                // Make route clickable to select it
                polyline.on('click', () => {
                    this.selectRoute(index);
                });
            }
        });

        // Second pass: draw selected route on top (thicker)
        const selectedRoute = this.currentRoutes[selectedIndex];
        const selectedPolyline = L.polyline(selectedRoute.coordinates, {
            color: colors[selectedIndex % colors.length],
            weight: 8,
            opacity: 1.0
        }).addTo(this.mapService.routeLayer);
    }

    displayRouteInfo(route) {
        const routeInfo = document.getElementById('routeInfo');
        const routeStats = document.getElementById('routeStats');

        let html = `
            <div class="stat-item">
                <span class="stat-label">Distance:</span>
                <span class="stat-value">${units.routeDistanceText(route)}</span>
            </div>
            <div class="stat-item">
                <span class="stat-label">Duration:</span>
                <span class="stat-value">${route.duration}</span>
            </div>
        `;

        // Add warnings if any
        if (route.warnings && route.warnings.length > 0) {
            html += '<div style="margin-top: 1rem;">';
            route.warnings.forEach(warning => {
                html += `
                    <div class="warning">
                        <strong>⚠️ ${warning.type.toUpperCase()}</strong>
                        ${warning.message}
                    </div>
                `;
            });
            html += '</div>';
        }

        routeStats.innerHTML = html;
        routeInfo.classList.remove('hidden');
    }

    displayElevationProfile(elevationData) {
        const chart = document.getElementById('elevationChart');
        const canvas = document.getElementById('elevationCanvas');
        const stats = document.getElementById('elevationStats');

        chart.classList.remove('hidden');

        // Draw elevation profile on canvas
        this.drawElevationChart(canvas, elevationData.points);

        // Display stats
        stats.innerHTML = `
            <div class="stat-item">
                <span class="stat-label">Total Gain:</span>
                <span class="stat-value">${units.elevationText(elevationData.stats.totalGain)}</span>
            </div>
            <div class="stat-item">
                <span class="stat-label">Total Loss:</span>
                <span class="stat-value">${units.elevationText(elevationData.stats.totalLoss)}</span>
            </div>
            <div class="stat-item">
                <span class="stat-label">Max Elevation:</span>
                <span class="stat-value">${units.elevationText(elevationData.stats.maxElevation)}</span>
            </div>
        `;
    }

    drawElevationChart(canvas, points) {
        const ctx = canvas.getContext('2d');
        const width = canvas.width = canvas.offsetWidth;
        const height = canvas.height = 150;

        // Clear canvas
        ctx.clearRect(0, 0, width, height);

        // Find min and max elevations
        const elevations = points.map(p => p.elevation);
        const minElev = Math.min(...elevations);
        const maxElev = Math.max(...elevations);
        const range = maxElev - minElev;

        // Draw gradient background
        const gradient = ctx.createLinearGradient(0, 0, 0, height);
        gradient.addColorStop(0, 'rgba(102, 126, 234, 0.3)');
        gradient.addColorStop(1, 'rgba(102, 126, 234, 0.05)');

        // Draw elevation profile
        ctx.beginPath();
        ctx.moveTo(0, height);

        points.forEach((point, index) => {
            const x = (index / (points.length - 1)) * width;
            const y = height - ((point.elevation - minElev) / range) * height;
            
            if (index === 0) {
                ctx.lineTo(x, y);
            } else {
                ctx.lineTo(x, y);
            }
        });

        ctx.lineTo(width, height);
        ctx.closePath();
        ctx.fillStyle = gradient;
        ctx.fill();

        // Draw line
        ctx.beginPath();
        points.forEach((point, index) => {
            const x = (index / (points.length - 1)) * width;
            const y = height - ((point.elevation - minElev) / range) * height;
            
            if (index === 0) {
                ctx.moveTo(x, y);
            } else {
                ctx.lineTo(x, y);
            }
        });

        ctx.strokeStyle = '#1e88e5';
        ctx.lineWidth = 2;
        ctx.stroke();
    }

    displayWindData(windData, rvHeight) {
        const overlay = document.getElementById('windOverlay');
        const windDataDiv = document.getElementById('windData');

        overlay.classList.remove('hidden');

        // Analyze wind impact
        const analysis = this.windService.analyzeWindImpact(windData, rvHeight);

        let html = '';

        // Show overall wind summary
        html += `
            <div style="margin-bottom: 1rem; padding: 1rem; background: #f8f9fa; border-radius: 4px;">
                <strong>Wind Summary:</strong><br>
                Average: ${Math.ceil(units.speed(analysis.avgWindSpeed))} ${units.unit.speed()} | 
                Max: ${Math.ceil(units.speed(analysis.maxWindSpeed))} ${units.unit.speed()} | 
                Risk Level: <span style="color: ${this.getRiskColor(analysis.overallRisk)}; font-weight: 600;">${analysis.overallRisk.toUpperCase()}</span>
            </div>
        `;

        // Show warnings if any
        if (analysis.warnings.length > 0) {
            html += '<div style="margin-bottom: 1rem;">';
            analysis.warnings.forEach(warning => {
                html += `
                    <div class="warning">
                        <strong>⚠️ Wind Alert</strong>
                        ${warning.message}<br>
                        <small>Location: ${warning.location} | Speed: ${units.speed(warning.speed)} ${units.unit.speed()} | Gust: ${units.speed(warning.gust)} ${units.unit.speed()}</small>
                    </div>
                `;
            });
            html += '</div>';
        }

        // Show wind points
        windData.slice(0, 6).forEach((point, index) => {
            html += `
                <div class="wind-point">
                    <div class="location">Point ${index + 1}</div>
                    <div class="speed">${units.speed(point.speed)} ${units.unit.speed()}</div>
                    <div class="direction">Direction: ${point.direction}°</div>
                    <div class="direction">${point.description}</div>
                </div>
            `;
        });

        windDataDiv.innerHTML = html;
    }

    getRiskColor(risk) {
        switch (risk) {
            case 'high': return '#F44336';
            case 'moderate': return '#FFC107';
            case 'low': return '#4CAF50';
            default: return '#666';
        }
    }

    /**
     * Add a city to the avoid list (from the avoidCityInput autocomplete's
     * onSelect). Just updates the chip list -- the preference is only
     * actually applied on the next Calculate Route click (RouteService
     * reads this.avoidCities fresh each time), same as every other route
     * option on this panel (e.g. avoidLowClearance).
     * Silently skipped if AWS didn't return a bounding box for this place
     * (MapView) -- a point address has nothing for Avoid.Areas to use.
     */
    addAvoidCity(place) {
        // Towns only: a street, address or landmark's area is a few metres
        // (Denver Ct 0.05 x 0.09 km, Ballarat Station a point) while towns
        // are many km across (Ballarat 18 x 25 km), so a picked street would
        // look like a whole city avoided but avoid nothing (2026-10-31).
        const b = place?.bbox;
        const widthKm = b ? (b.east - b.west) * 111.32 * Math.cos(((b.north + b.south) / 2) * Math.PI / 180) : 0;
        const heightKm = b ? (b.north - b.south) * 110.57 : 0;
        if (Math.min(widthKm, heightKm) < 0.5) {
            if (window.showToast) window.showToast('Pick a town or city to avoid');
            return;
        }
        const label = this.pickedPlaceLabel(place);
        if (this.avoidCities.some(c => c.label === label)) return;
        this.avoidCities.push({ label, bbox: place.bbox });
        this.renderAvoidCities();
    }

    /**
     * What was actually picked ("Denver Ct", "Ballarat"), not the city it's
     * in -- place.city for a street in a suburb is the metro ("Melbourne").
     */
    pickedPlaceLabel(place) {
        return (place.formattedAddress || '').split(',')[0].trim() || place.city || 'Unknown';
    }

    removeAvoidCity(index) {
        this.avoidCities.splice(index, 1);
        this.renderAvoidCities();
    }

    renderAvoidCities() {
        const container = document.getElementById('avoidCitiesList');
        if (!container) return;
        container.innerHTML = this.avoidCities.map((c, i) => `
            <span class="avoid-city-chip" style="display:inline-flex;align-items:center;gap:0.3rem;background:#2a2a3e;color:#fff;border-radius:12px;padding:0.25rem 0.5rem;font-size:0.8rem;">
                ${escapeHtml(c.label)}
                <button type="button" data-avoid-index="${i}" aria-label="Remove ${escapeHtml(c.label)}" style="background:none;border:none;color:#F5A623;cursor:pointer;font-weight:bold;padding:0;">×</button>
            </span>
        `).join('');
        container.querySelectorAll('[data-avoid-index]').forEach(btn => {
            btn.addEventListener('click', () => this.removeAvoidCity(parseInt(btn.dataset.avoidIndex, 10)));
        });
    }

    /**
     * Add a city to force the route through (from throughCityInput's
     * onSelect). Only needs a point, not a bbox -- preferences.use is a
     * waypoint hint, not an area. Same timing as avoidCities: just updates
     * the chip list, applied on the next Calculate Route click.
     */
    addThroughCity(place) {
        if (typeof place?.lat !== 'number' || typeof place?.lng !== 'number') return;
        const label = this.pickedPlaceLabel(place);
        if (this.throughCities.some(c => c.label === label)) return;
        this.throughCities.push({ label, lat: place.lat, lng: place.lng });
        this.renderThroughCities();
    }

    removeThroughCity(index) {
        this.throughCities.splice(index, 1);
        this.renderThroughCities();
    }

    renderThroughCities() {
        const container = document.getElementById('throughCitiesList');
        if (!container) return;
        container.innerHTML = this.throughCities.map((c, i) => `
            <span class="through-city-chip" style="display:inline-flex;align-items:center;gap:0.3rem;background:#2a2a3e;color:#fff;border-radius:12px;padding:0.25rem 0.5rem;font-size:0.8rem;">
                ${escapeHtml(c.label)}
                <button type="button" data-through-index="${i}" aria-label="Remove ${escapeHtml(c.label)}" style="background:none;border:none;color:#F5A623;cursor:pointer;font-weight:bold;padding:0;">×</button>
            </span>
        `).join('');
        container.querySelectorAll('[data-through-index]').forEach(btn => {
            btn.addEventListener('click', () => this.removeThroughCity(parseInt(btn.dataset.throughIndex, 10)));
        });
    }

    /**
     * Get stops for a specific route
     */
    getRouteStops(routeIndex) {
        if (routeIndex === undefined) {
            routeIndex = this.selectedRouteIndex !== undefined ? this.selectedRouteIndex : this.bestRouteIndex;
        }
        return this.routeStopsMap[routeIndex] || [];
    }
    
    /**
     * Route again from start to destination through these stops (in order
     * along the route), keeping them: the normal Calculate Route flow, so
     * wind, grades, clearances and fuel all match the new line.
     */
    recalculateThroughStops(stops) {
        this._carryStops = (stops || []).map(s => ({ ...s }));
        if (window.showToast) window.showToast(this._carryStops.length ? 'Updating your route through your stops…' : 'Updating your route…');
        document.getElementById('calculateRoute')?.click();
    }

    /**
     * Add stop to current route
     */
    addStopToCurrentRoute(stop) {
        const routeIndex = this.selectedRouteIndex !== undefined ? this.selectedRouteIndex : this.bestRouteIndex;
        if (!this.routeStopsMap[routeIndex]) {
            this.routeStopsMap[routeIndex] = [];
        }
        this.routeStopsMap[routeIndex].push(stop);
    }
    
    /**
     * Remove stop from current route
     */
    removeStopFromCurrentRoute(stopIndex) {
        const routeIndex = this.selectedRouteIndex !== undefined ? this.selectedRouteIndex : this.bestRouteIndex;
        if (this.routeStopsMap[routeIndex]) {
            this.routeStopsMap[routeIndex].splice(stopIndex, 1);
            if (this.routeStopsMap[routeIndex].length === 0) {
                delete this.routeStopsMap[routeIndex];
            }
        }
    }

    /**
     * Calculate distance along route to a point
     */
    calculateDistanceAlongRoute(lat, lng, coordinates) {
        let minDistance = Infinity;
        let nearestIndex = 0;
        
        // Find nearest point on route
        for (let i = 0; i < coordinates.length; i++) {
            const dist = Math.sqrt(
                Math.pow(coordinates[i].lat - lat, 2) + 
                Math.pow(coordinates[i].lng - lng, 2)
            );
            if (dist < minDistance) {
                minDistance = dist;
                nearestIndex = i;
            }
        }
        
        // Calculate cumulative distance to that point
        let distance = 0;
        for (let i = 0; i < nearestIndex; i++) {
            distance += this.calculateDistance(
                coordinates[i].lat,
                coordinates[i].lng,
                coordinates[i + 1].lat,
                coordinates[i + 1].lng
            );
        }
        
        return distance;
    }

    /**
     * Sort stops by their position along the route
     */
    sortStopsByRoutePosition(routeIndex) {
        if (!this.routeStopsMap[routeIndex]) return;
        
        this.routeStopsMap[routeIndex].sort((a, b) => {
            return (a.distanceAlongRoute || 0) - (b.distanceAlongRoute || 0);
        });
        
        console.log('✓ Stops sorted by route position');
    }

    /**
     * Add a gas station as a waypoint/stop on the route
     */
    async addStopToRoute(lat, lng, name, markerId) {
        console.log(`Adding stop: ${name} at ${lat}, ${lng}`);
        
        // Close the popup
        if (markerId) {
            const marker = this.mapService.gasStationMarkers.find(m => m._customId === markerId);
            if (marker) {
                marker.closePopup();
            }
        }
        
        // Get current destination
        const destInput = document.getElementById('destination');
        
        if (!destInput.value || !this.currentRoutes) {
            alert('Please calculate a route first before adding stops');
            return;
        }
        
        // Get the current route
        const routeIndex = this.selectedRouteIndex !== undefined ? this.selectedRouteIndex : this.bestRouteIndex;
        const route = this.currentRoutes[routeIndex];
        
        // Calculate distance along route for this stop
        const distanceAlongRoute = this.calculateDistanceAlongRoute(lat, lng, route.coordinates);
        
        // Store the stop for this specific route
        this.addStopToCurrentRoute({
            lat,
            lng,
            name,
            timestamp: new Date(),
            distanceFromPrevious: 0, // Will be calculated in updateTripSummary
            distanceAlongRoute: distanceAlongRoute
        });
        
        // Sort stops by their position along the route
        this.sortStopsByRoutePosition(routeIndex);
        
        const routeStops = this.getRouteStops(routeIndex);
        console.log(`✓ Stop ${routeStops.length} added to Route ${routeIndex + 1}: ${name}`);

        // The route itself now goes through the stops (and navigation with it).
        this.recalculateThroughStops(routeStops);
        return;
        
        // Recalculate wind profile with the new stop included
        const showWindIcons = document.getElementById('showWindIcons').checked;
        if (showWindIcons && route.timeAwareWindProfile) {
            console.log('🌬️ Recalculating wind profile with new stop...');
            
            // Get departure date/time from form inputs
            const departureDateInput = document.getElementById('departureDate');
            const departureTimeInput = document.getElementById('departureTime');
            
            let departureDateTime = new Date();
            if (departureDateInput && departureDateInput.value) {
                const dateStr = departureDateInput.value;
                const timeStr = departureTimeInput && departureTimeInput.value ? departureTimeInput.value : '08:00';
                departureDateTime = new Date(`${dateStr}T${timeStr}`);
            }
            
            // Get RV specs from profile
            const rvSpecs = {
                type: window.currentProfile?.rvType || 'travel-trailer',
                height: window.currentProfile?.height || 10,
                weight: window.currentProfile?.weight || 5000,
                length: window.currentProfile?.length || 25
            };
            
            // Prepare waypoints with the new stop included
            const waypoints = this.prepareWaypointsForWindForecast(route.coordinates, routeIndex, route.pinnedWindPoint);
            
            // Rebuild time-aware wind profile
            const timeAwareWindProfile = await this.windService.buildRouteWindProfile(
                departureDateTime,
                waypoints,
                rvSpecs,
                window.travelPreferences || {}
            );
            
            // Update the route's wind profile
            route.timeAwareWindProfile = timeAwareWindProfile;
            
            // Phase 19: Attach original wind data to the pinned point for comparison display
            if (route.pinnedWindPoint) {
                const pinnedInProfile = timeAwareWindProfile.windProfile.find(wp => wp.isPinnedWindPoint);
                if (pinnedInProfile) {
                    pinnedInProfile.originalSpeed = route.pinnedWindPoint.originalSpeed;
                    pinnedInProfile.originalGust = route.pinnedWindPoint.originalGust;
                    pinnedInProfile.originalArrivalTime = route.pinnedWindPoint.originalArrivalTime;
                }
            }
            
            // Re-add wind markers with updated times (addWindMarkers clears old ones first)
            this.mapService.addWindMarkers(timeAwareWindProfile.windProfile);
            
            console.log(`✓ Wind profile recalculated with ${routeStops.length} stop(s)`);
        }
        
        // Recalculate gas station distances from this new starting point
        this.recalculateGasStationDistances(lat, lng);
        
        // Update trip summary display
        await this.updateTripSummary();
        
        // Re-center map to show full route after adding stop
        setTimeout(() => {
            this.mapService.fitRouteToView(route.coordinates);
        }, 100);
    }
    
    /**
     * Recalculate gas station distances from a new starting point
     */
    recalculateGasStationDistances(newStartLat, newStartLng) {
        console.log(`Recalculating distances from new start point: ${newStartLat}, ${newStartLng}`);
        
        // Get the current route coordinates
        const routeIndex = this.selectedRouteIndex !== undefined ? this.selectedRouteIndex : this.bestRouteIndex;
        const route = this.currentRoutes[routeIndex];
        if (!route || !route.coordinates) {
            return;
        }
        
        // Find the index of the new starting point in the route
        let startIndex = 0;
        let minDist = Infinity;
        
        route.coordinates.forEach((coord, index) => {
            const dist = Math.sqrt(
                Math.pow(coord.lat - newStartLat, 2) + 
                Math.pow(coord.lng - newStartLng, 2)
            );
            if (dist < minDist) {
                minDist = dist;
                startIndex = index;
            }
        });
        
        // Create a new coordinate array starting from this point
        const remainingRoute = route.coordinates.slice(startIndex);
        
        console.log(`Route now starts at index ${startIndex}, ${remainingRoute.length} points remaining`);
        
        // Re-fetch gas stations with the remaining route to get updated distances
        const showGasStations = document.getElementById('showGasStations').checked;
        if (showGasStations) {
            // Don't clear existing gas stations, just update distances for the remaining route
            console.log('🔄 Updating gas station distances for remaining route');
            
            // Note: We're not calling displayGasStations here because it would clear existing markers
            // Instead, we keep all gas stations visible and they'll show updated distances
            // when the user hovers over them or if we implement a refresh mechanism
            
            // Re-add wind markers if they exist for this route
            const showWindIcons = document.getElementById('showWindIcons').checked;
            if (showWindIcons && route.timeAwareWindProfile) {
                console.log('🌬️ Re-adding wind markers');
                this.mapService.addWindMarkers(route.timeAwareWindProfile.windProfile);
            }
        }
    }
    
    /**
     * Reverse geocode coordinates to get city, state
     */
    async reverseGeocode(lat, lng) {
        const cacheKey = `${lat},${lng}`;
        if (this.geocodeCache.has(cacheKey)) {
            return this.geocodeCache.get(cacheKey);
        }

        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=10`
            );
            const data = await response.json();

            let result;
            if (data && data.address) {
                const city = data.address.city || data.address.town || data.address.village || data.address.county;
                const state = data.address.state;

                if (city && state) {
                    result = `${city}, ${state}`;
                } else if (city) {
                    result = city;
                } else if (state) {
                    result = state;
                }
            }

            if (!result) result = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
            this.geocodeCache.set(cacheKey, result);
            return result;
        } catch (error) {
            console.error('Reverse geocoding error:', error);
            // Not cached -- a transient network failure shouldn't permanently
            // stick a coordinate-only fallback in place of its real name.
            return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
        }
    }

    /**
     * Find the wind forecast nearest a given point along the route's
     * time-aware wind profile. Shared by updateTripSummary() (initial build)
     * and refreshWindForDepartureTime() (slider-driven refresh) so both
     * compute wind badges the same way.
     */
    findNearestWindData(route, lat, lng) {
        if (!route.timeAwareWindProfile) return null;
        const windProfile = route.timeAwareWindProfile.windProfile;
        let minDist = Infinity;
        let nearestWaypoint = null;

        for (const waypoint of windProfile) {
            const dist = Math.sqrt(
                Math.pow(waypoint.lat - lat, 2) +
                Math.pow(waypoint.lng - lng, 2)
            );
            if (dist < minDist) {
                minDist = dist;
                nearestWaypoint = waypoint;
            }
        }

        // Wind waypoints are sampled every 25 miles, so a stop can legitimately
        // fall up to ~12.5 miles from the nearest one -- 0.188 degrees =~ 13
        // miles, just over half the sampling gap, so every stop has a sample
        // in range.
        if (nearestWaypoint && minDist < 0.188) {
            return {
                speed: nearestWaypoint.wind.speed,
                cautionExceeded: nearestWaypoint.cautionExceeded,
                dangerExceeded: nearestWaypoint.dangerExceeded
            };
        }
        return null;
    }

    /**
     * Render the stop-by-stop segment list + overall wind summary into
     * #tripSegments. Wind badges are computed fresh from
     * route.timeAwareWindProfile here rather than stored on the segment, so
     * this same function serves both the initial geocoded build
     * (updateTripSummary) and a later wind-only refresh (the Pro departure
     * time slider, see refreshWindForDepartureTime) that reuses the same
     * `segments` -- same resolved location names -- against a newly
     * recalculated wind profile, with no re-geocoding.
     * @returns {number} total trip distance in miles
     */
    renderTripSegments(route, segments) {
        const tripSegments = document.getElementById('tripSegments');
        const totalDistance = segments.reduce((sum, seg) => sum + (seg.distance || 0), 0);

        let html = segments.map((segment, index) => {
            const iconClass = segment.type;
            const icon = segment.icon;
            let removeBtn = '';
            let windBadge = '';

            if (segment.type === 'stop') {
                removeBtn = `<button class="stop-remove" onclick="window.removeStopFromTrip(${segment.number - 1})" title="Remove this stop">✕</button>`;

                const windData = this.findNearestWindData(route, segment.lat, segment.lng);
                if (windData) {
                    let windColor = '#4CAF50'; // Green
                    if (windData.dangerExceeded) {
                        windColor = '#F44336'; // Red
                    } else if (windData.cautionExceeded) {
                        windColor = '#FFC107'; // Yellow
                    }
                    windBadge = `<div style="font-size: 0.75rem; color: ${windColor}; font-weight: 600; margin-top: 0.25rem;">🌬️ ${Math.ceil(units.speed(windData.speed))} ${units.unit.speed()}</div>`;
                }
            }

            return `
                ${index > 0 && segment.distance ? `<div style="text-align: center; color: #1e88e5; font-weight: 600; font-size: 0.85rem; padding: 0.25rem 0;">${units.distanceText(segment.distance, { decimals: 1 })}</div>` : ''}
                <div class="trip-segment">
                    <div class="segment-icon ${iconClass}">${icon}</div>
                    <div class="segment-info">
                        <div class="segment-name">${segment.name}</div>
                        <div class="segment-total">${segment.location}</div>
                        ${windBadge}
                    </div>
                    ${removeBtn}
                </div>
            `;
        }).join('');

        let windSummary = '';
        if (route.timeAwareWindProfile) {
            const maxWind = Math.ceil(route.timeAwareWindProfile.maxWindSpeed);
            const hasCautions = route.timeAwareWindProfile.hasCautions;
            const hasDangers = route.timeAwareWindProfile.hasDangers;

            let windColor = '#4CAF50'; // Green
            let windIcon = '🌬️';
            let windStatus = 'Good';

            if (hasDangers) {
                windColor = '#F44336'; // Red
                windIcon = '⚠️';
                windStatus = 'Dangerous';
            } else if (hasCautions) {
                windColor = '#FFC107'; // Yellow
                windIcon = '⚠️';
                windStatus = 'Caution';
            }

            windSummary = `
                <div style="margin-top: 0.5rem; padding: 0.75rem; background: ${windColor}15; border-radius: 6px; border-left: 4px solid ${windColor};">
                    <div style="display: flex; align-items: center; justify-content: space-between;">
                        <div style="display: flex; align-items: center; gap: 0.5rem;">
                            <span style="font-size: 1.25rem;">${windIcon}</span>
                            <div>
                                <div style="font-weight: 600; color: ${windColor};">Wind: ${windStatus}</div>
                                <div style="font-size: 0.85rem; color: #666;">Max ${units.speed(maxWind)} ${units.unit.speed()}</div>
                            </div>
                        </div>
                        <div style="font-size: 1.5rem; font-weight: bold; color: ${windColor};">${units.speed(maxWind)} ${units.unit.speed()}</div>
                    </div>
                </div>
            `;
        }

        html += `
            <div style="margin-top: 0.75rem; padding-top: 0.75rem; border-top: 2px solid #1e88e5; text-align: center; font-weight: bold; color: #1e88e5;">
                Total Trip: ${units.distanceText(totalDistance, { decimals: 1, long: true })}
            </div>
            ${windSummary}
        `;

        tripSegments.innerHTML = html;
        return totalDistance;
    }

    /**
     * Phase 27 (Pro): recompute the selected route's wind forecast for a
     * different departure time -- no re-routing, no re-geocoding. Mirrors
     * the recalculation addStopToCurrentRoute() already does after a stop
     * is added, but triggered by the departure time slider instead.
     */
    async refreshWindForDepartureTime(departureDateTime) {
        const routes = this.currentRoutes;
        // While a new plan is still being analysed these are its preview
        // routes (no wind yet), and the analysis would overwrite the result
        // with the planned time; the next slider move applies.
        if (!routes?.length || window.routeAnalysisPending) return;

        const rvSpecs = {
            type: window.currentProfile?.rvType || 'travel-trailer',
            height: window.currentProfile?.height || 10,
            weight: window.currentProfile?.weight || 5000,
            length: window.currentProfile?.length || 25
        };

        // Every route option, so the comparison and the recommended route
        // follow the slider too. All are fetched before any is applied, so a
        // failure leaves every route on the same (previous) time.
        const profiles = await Promise.all(routes.map((route, i) => {
            const waypoints = this.prepareWaypointsForWindForecast(route.coordinates, i, route.pinnedWindPoint, route.windHotspots || []);
            return this.windService.buildRouteWindProfile(
                departureDateTime,
                waypoints,
                rvSpecs,
                window.travelPreferences || {}
            );
        }));

        routes.forEach((route, i) => {
            const timeAwareWindProfile = profiles[i];
            route.timeAwareWindProfile = timeAwareWindProfile;
            route.windData = timeAwareWindProfile.windProfile.map(wp => wp.wind);
            route.windAnalysis = this.buildWindAnalysis(timeAwareWindProfile);

            // Phase 19: Attach original wind data to the pinned point for comparison display
            if (route.pinnedWindPoint) {
                const pinnedInProfile = timeAwareWindProfile.windProfile.find(wp => wp.isPinnedWindPoint);
                if (pinnedInProfile) {
                    pinnedInProfile.originalSpeed = route.pinnedWindPoint.originalSpeed;
                    pinnedInProfile.originalGust = route.pinnedWindPoint.originalGust;
                    pinnedInProfile.originalArrivalTime = route.pinnedWindPoint.originalArrivalTime;
                }
            }
        });

        // Re-pick the recommended route for the new time, with planning's
        // low-clearance rule: a route with a clearance the rig won't fit
        // under gives way to the first route without one.
        let best = this.selectBestRoute(routes, this.lastRvSpecs || rvSpecs);
        // Only this plan's clearance results (they're never cleared between plans).
        const clearanceWarnings = this.clearanceWarningsFor === routes ? window._routeClearanceWarnings : null;
        const routeDangers = (clearanceWarnings || [])
            .map(warnings => (warnings || []).filter(w => w.severity === 'danger').length);
        if (routeDangers[best.index] > 0) {
            const saferIdx = routeDangers.findIndex(d => d === 0);
            if (saferIdx !== -1) {
                // Reasons for the route actually recommended, not the top scorer.
                const safer = this.selectBestRoute([routes[saferIdx]], this.lastRvSpecs || rvSpecs);
                best = { ...safer, index: saferIdx, route: routes[saferIdx] };
            }
        }
        const recommendationChanged = best.index !== this.bestRouteIndex;
        this.bestRouteIndex = best.index;

        // Redraw the comparison, keeping each route's road-quality box
        // (filled in separately, for the route it was fetched for).
        const roadQualitySlots = routes.map((_, i) => document.getElementById(`roadQualitySlot-${i}`)?.innerHTML || '');
        this.displayRouteComparison(routes, best);
        roadQualitySlots.forEach((html, i) => {
            const slot = document.getElementById(`roadQualitySlot-${i}`);
            if (slot && html) slot.innerHTML = html;
        });

        // No route tapped: the shown route follows the recommendation,
        // switching everything a tap would (map, wind, fuel, card...).
        if (recommendationChanged && this.selectedRouteIndex === undefined) {
            this.selectRoute(best.index, { userChoice: false });
            return;
        }

        const routeIndex = this.selectedRouteIndex !== undefined ? this.selectedRouteIndex : this.bestRouteIndex;
        const route = routes[routeIndex];
        const timeAwareWindProfile = route.timeAwareWindProfile;

        const showWindIcons = document.getElementById('showWindIcons')?.checked;
        if (showWindIcons) {
            this.mapService.addWindMarkers(timeAwareWindProfile.windProfile);
        }

        // Refresh trip-summary wind badges from the cached (already-geocoded)
        // segments -- only if they still match this route's current stops.
        const routeStops = this.getRouteStops(routeIndex);
        const stopKey = (routeStops || []).map(s => `${s.lat},${s.lng}`).join('|');
        if (this.lastTripSegments
            && this.lastTripSegments.routeIndex === routeIndex
            && this.lastTripSegments.stopKey === stopKey) {
            this.renderTripSegments(route, this.lastTripSegments.segments);
        }
    }

    /**
     * A route's wind summary (average, max, alerts) from its time-aware wind
     * profile. Used at planning and again by the departure slider, so the
     * route card and comparison follow the chosen time.
     */
    buildWindAnalysis(timeAwareWindProfile) {
        const windSpeeds = timeAwareWindProfile.windProfile
            .map(wp => wp.wind?.speed)
            .filter(s => typeof s === 'number' && !isNaN(s));
        const avgWind = windSpeeds.length > 0
            ? windSpeeds.reduce((sum, s) => sum + s, 0) / windSpeeds.length
            : 0;
        return {
            maxWindSpeed: timeAwareWindProfile.maxWindSpeed || 0,
            avgWindSpeed: avgWind,
            warnings: timeAwareWindProfile.windProfile
                .filter(wp => wp.cautionExceeded || wp.dangerExceeded)
                .map(wp => ({
                    location: `${wp.lat.toFixed(2)}, ${wp.lng.toFixed(2)}`,
                    speed: wp.wind.speed,
                    gust: wp.wind.gust,
                    arrivalTime: new Date(wp.arrivalTime).toLocaleTimeString(displayLocale(), { hour: 'numeric', minute: '2-digit' }),
                    severity: wp.dangerExceeded ? 'high' : 'moderate',
                    message: wp.dangerExceeded
                        ? `Dangerous wind conditions (${units.speed(wp.wind.speed)} ${units.unit.speed()}, gusts ${units.speed(wp.wind.gust)} ${units.unit.speed()}) expected at ${new Date(wp.arrivalTime).toLocaleTimeString(displayLocale(), { hour: 'numeric', minute: '2-digit' })} - Don't drive!`
                        : `Caution advised (${units.speed(wp.wind.speed)} ${units.unit.speed()}) expected at ${new Date(wp.arrivalTime).toLocaleTimeString(displayLocale(), { hour: 'numeric', minute: '2-digit' })} - Drive carefully`
                })),
            overallRisk: timeAwareWindProfile.hasDangers ? 'high' : (timeAwareWindProfile.hasCautions ? 'moderate' : 'low'),
            timeAware: true
        };
    }

    /**
     * Calculate distance between two points in miles
     */
    calculateDistance(lat1, lng1, lat2, lng2) {
        const R = 3959; // Earth's radius in miles
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLng = (lng2 - lng1) * Math.PI / 180;
        
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                  Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                  Math.sin(dLng / 2) * Math.sin(dLng / 2);
        
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    }
    
    /**
     * Update trip summary with all stops
     */
    async updateTripSummary() {
        const tripSummary = document.getElementById('tripSummary');
        const tripSummaryTitle = document.getElementById('tripSummaryTitle');
        const tripSegments = document.getElementById('tripSegments');
        
        // Get stops for current route
        const routeIndex = this.selectedRouteIndex !== undefined ? this.selectedRouteIndex : this.bestRouteIndex;
        const routeStops = this.getRouteStops(routeIndex);
        
        if (!routeStops || routeStops.length === 0) {
            tripSummary.classList.add('hidden');
            this.lastTripSegments = null;
            return;
        }
        
        tripSummary.classList.remove('hidden');
        
        // Update title to show which route
        tripSummaryTitle.textContent = `Your Trip - Route ${routeIndex + 1}`;
        
        tripSegments.innerHTML = '<div style="text-align: center; color: #999;">Loading locations...</div>';

        // Get route info - use selected route if available, otherwise best route
        const route = this.currentRoutes[routeIndex];
        const startCoords = route.coordinates[0];
        const endCoords = route.coordinates[route.coordinates.length - 1];

        // Build segments with geocoding
        let segments = [];

        // Original start
        const startLocation = await this.reverseGeocode(startCoords.lat, startCoords.lng);
        segments.push({
            type: 'start',
            name: 'Start',
            location: startLocation,
            icon: '🏁',
            lat: startCoords.lat,
            lng: startCoords.lng
        });

        // All stops with distances
        let prevLat = startCoords.lat;
        let prevLng = startCoords.lng;

        for (let i = 0; i < routeStops.length; i++) {
            const stop = routeStops[i];
            const stopLocation = await this.reverseGeocode(stop.lat, stop.lng);
            const distance = this.calculateDistance(prevLat, prevLng, stop.lat, stop.lng);

            segments.push({
                type: 'stop',
                name: stop.name,
                location: stopLocation,
                icon: '⛽',
                number: i + 1,
                distance: distance,
                lat: stop.lat,
                lng: stop.lng
            });

            prevLat = stop.lat;
            prevLng = stop.lng;
        }

        // Final destination
        const destLocation = await this.reverseGeocode(endCoords.lat, endCoords.lng);
        const finalDistance = this.calculateDistance(prevLat, prevLng, endCoords.lat, endCoords.lng);

        segments.push({
            type: 'end',
            name: 'Destination',
            location: destLocation,
            icon: '🏁',
            distance: finalDistance,
            lat: endCoords.lat,
            lng: endCoords.lng
        });

        const totalDistance = this.renderTripSegments(route, segments);

        // Phase 27: cache the geocoded segments (no network calls needed to
        // re-render them) keyed by route + exact stop coordinates, so the
        // departure-time slider's wind-only refresh can reuse this instead
        // of re-resolving every stop's name through Nominatim on every drag.
        // A stop added/removed changes stopKey, which invalidates the cache.
        this.lastTripSegments = {
            routeIndex,
            stopKey: routeStops.map(s => `${s.lat},${s.lng}`).join('|'),
            segments
        };

        // Phase 6 Task 14: Show stop suggestions based on drive time
        const routeCoords = route.coordinates || [];
        const stopSuggestions = this.suggestStops(totalDistance, segments, routeCoords);
        if (stopSuggestions.length > 0) {
            // Reverse geocode suggestion coordinates to get city names
            const geocodedSuggestions = await Promise.all(stopSuggestions.map(async (s) => {
                if (s.coord) {
                    const location = await this.reverseGeocode(s.coord.lat, s.coord.lng);
                    if (location) {
                        s.nearestStop = location;
                        s.message = s.type === 'warning'
                            ? `You'll reach your drive limit near ${location}. A stop is strongly recommended.`
                            : `Consider a stop near ${location} — you'll have been driving ~${s.atMile / (window.travelPreferences?.avgCruisingSpeedMph || 60)} hours.`.replace(/(\d+\.\d{2,})/g, m => parseFloat(m).toFixed(0));
                    }
                }
                return s;
            }));

            tripSegments.innerHTML += geocodedSuggestions.map(s => {
                const color = s.type === 'warning' ? '#ff9800' : '#1e88e5';
                const icon = s.type === 'warning' ? '⚠️' : '⏱';
                return `
                    <div style="margin-top: 0.75rem; padding: 0.75rem; background: ${s.type === 'warning' ? '#fff3e0' : '#e3f2fd'}; border-radius: 8px; border-left: 4px solid ${color};">
                        <div style="font-weight: 600; color: ${color}; font-size: 0.85rem;">${icon} ${s.message}</div>
                        ${s.nearestStop ? `<div style="font-size: 0.8rem; color: #546e7a; margin-top: 0.25rem;">📍 ${s.nearestStop}</div>` : ''}
                    </div>
                `;
            }).join('');
        }
        
        // Expose remove function
        window.removeStopFromTrip = (index) => {
            this.removeStopFromTrip(index);
        };
        
        // Add markers for stops on the map
        this.addStopMarkersToMap();

        // Phase 15: Update the floating route card (duration changes with stops)
        if (window.updateRouteCard) window.updateRouteCard();
    }
    
    /**
     * Add markers for all stops on the map
     */
    addStopMarkersToMap() {
        // Clear existing stop markers
        if (this.stopMarkers) {
            this.stopMarkers.forEach(marker => marker.remove());
        }
        this.stopMarkers = [];
        
        // Get stops for current route
        const routeIndex = this.selectedRouteIndex !== undefined ? this.selectedRouteIndex : this.bestRouteIndex;
        const routeStops = this.getRouteStops(routeIndex);
        
        console.log(`🗺️ Adding ${routeStops.length} stop markers to map`);
        
        // Add marker for each stop
        if (routeStops && routeStops.length > 0) {
            routeStops.forEach((stop, index) => {
                const marker = this.mapService.addMarker(
                    stop.lat,
                    stop.lng,
                    `Stop ${index + 1}: ${stop.name}`,
                    'orange'
                );
                this.stopMarkers.push(marker);
                console.log(`  ✓ Added stop marker ${index + 1} at ${stop.lat}, ${stop.lng}`);
            });
            
            // Update destination marker to show distance from last stop
            this.updateDestinationMarker();
        }
    }
    
    /**
     * Update destination marker with distance from last stop
     */
    updateDestinationMarker() {
        const routeIndex = this.selectedRouteIndex !== undefined ? this.selectedRouteIndex : this.bestRouteIndex;
        const route = this.currentRoutes[routeIndex];
        const routeStops = this.getRouteStops(routeIndex);
        
        if (!route || !routeStops || routeStops.length === 0) {
            return;
        }
        
        const lastStop = routeStops[routeStops.length - 1];
        const destCoords = route.coordinates[route.coordinates.length - 1];
        
        // Find the index of the last stop in the route
        let startIndex = 0;
        let minDist = Infinity;
        
        route.coordinates.forEach((coord, index) => {
            const dist = Math.sqrt(
                Math.pow(coord.lat - lastStop.lat, 2) + 
                Math.pow(coord.lng - lastStop.lng, 2)
            );
            if (dist < minDist) {
                minDist = dist;
                startIndex = index;
            }
        });
        
        // Calculate distance from last stop to destination
        let distanceToDestination = 0;
        for (let i = startIndex; i < route.coordinates.length - 1; i++) {
            const lat1 = route.coordinates[i].lat;
            const lng1 = route.coordinates[i].lng;
            const lat2 = route.coordinates[i + 1].lat;
            const lng2 = route.coordinates[i + 1].lng;
            
            // Haversine distance
            const R = 3959; // Earth's radius in miles
            const dLat = (lat2 - lat1) * Math.PI / 180;
            const dLng = (lng2 - lng1) * Math.PI / 180;
            const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                      Math.sin(dLng / 2) * Math.sin(dLng / 2);
            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
            distanceToDestination += R * c;
        }
        
        // Find and update the destination marker
        const destMarker = this.mapService.markers.find(m => {
            const latlng = m.getLatLng();
            return Math.abs(latlng.lat - destCoords.lat) < 0.0001 && 
                   Math.abs(latlng.lng - destCoords.lng) < 0.0001;
        });
        
        if (destMarker) {
            const destInput = document.getElementById('destination');
            const destLabel = `<strong>Destination</strong><br>${destInput.value}<br><br><strong>📍 ${units.distanceText(distanceToDestination, { decimals: 1, long: true })}</strong> from last stop`;
            destMarker.setPopupContent(destLabel);
        }
    }
    
    /**
     * Remove a stop from the trip
     */
    async removeStopFromTrip(index) {
        const routeIndex = this.selectedRouteIndex !== undefined ? this.selectedRouteIndex : this.bestRouteIndex;
        const routeStops = this.getRouteStops(routeIndex);
        
        if (!routeStops || index < 0 || index >= routeStops.length) {
            return;
        }
        
        const stop = routeStops[index];
        const confirmed = confirm(`Remove stop: ${stop.name}?`);
        
        if (confirmed) {
            this.removeStopFromCurrentRoute(index);
            
            // If no more stops, hide the trip summary
            const updatedStops = this.getRouteStops(routeIndex);
            if (updatedStops.length === 0) {
                document.getElementById('tripSummary').classList.add('hidden');
                // Clear stop markers
                if (this.stopMarkers) {
                    this.stopMarkers.forEach(marker => marker.remove());
                    this.stopMarkers = [];
                }
            } else {
                // Update the display
                await this.updateTripSummary();
            }
        }
    }

    /**
     * The routes on the map as soon as AWS returns them, before wind and
     * elevation are checked: draws them (first route highlighted), opens the
     * map with the route card showing distance and drive time, and holds the
     * Navigate button (window.routeAnalysisPending) until calculateRoute has
     * the full analysis, which then redraws and refreshes the card.
     */
    showRoutePreview(routeData) {
        const routes = routeData?.routes || [];
        if (!routes.length || !routes[0].coordinates?.length) return;
        window.routeAnalysisPending = true;
        this.currentRoutes = routes;
        this.bestRouteIndex = 0;
        this.selectedRouteIndex = undefined; // a new calculation: no route picked yet
        this.mapService.clearRoute();
        this.mapService.clearMarkers();
        // The previous route's wind badges and clearance warnings don't belong
        // to this one; the analysis adds the new ones.
        (this.mapService.windMarkers || []).forEach(m => m.remove());
        this.mapService.windMarkers = [];
        (this.mapService.clearanceMarkers || []).forEach(m => m.remove());
        this.mapService.clearanceMarkers = [];
        this.mapService.drawMultipleRoutes(routes, 0, () => {}); // picking waits for the analysis
        const coords = routes[0].coordinates;
        this.mapService.addMarker(coords[0].lat, coords[0].lng, 'Start: ' + escapeHtml(routeData.startAddress || ''), 'green');
        this.mapService.addMarker(coords[coords.length - 1].lat, coords[coords.length - 1].lng, '<strong>Destination</strong><br>' + escapeHtml(routeData.endAddress || ''), 'red');
        if (window.showMobileMapToggle) window.showMobileMapToggle();
        if (window.innerWidth > 1024 && window.showRouteCard) window.showRouteCard();
    }

    showLoading() {
        const btn = document.getElementById('calculateRoute');
        btn.textContent = 'Calculating...';
        btn.disabled = true;
    }

    hideLoading() {
        const btn = document.getElementById('calculateRoute');
        btn.textContent = 'Calculate Route';
        btn.disabled = false;
    }

    /**
     * Update the route counter display
     * v1.3: Old header counter removed — this now delegates to updateGoogleMapsButton()
     */
    updateRouteCounter() {
        this.updateGoogleMapsButton();
    }

    /**
     * Phase 3 Task 8: Show badge notification when a new badge is earned.
     * Fires at the moment of trip save per spec Section 9.
     * @param {Array} newBadges - Array of badge IDs just earned
     */
    showBadgeNotification(newBadges) {
        if (!newBadges || newBadges.length === 0) return;

        const badgeDefs = {
            founding_member: { name: 'Founding Member', emoji: '🏕️', description: 'One of the first 100 Zephyr members', shareMessage: 'Just became a Founding Member of Zephyr — the RV trip planner built around wind and elevation. Claim your spot 👉 zephyr.travel' },
            early_explorer: { name: 'Early Explorer', emoji: '⭐', description: 'Joined Zephyr in the first 500 members', shareMessage: 'Just earned the Early Explorer badge on Zephyr. One of the first 500 members of the smartest RV trip planner on the road.' },
            trail_blazer: { name: 'Trail Blazer', emoji: '🛠️', description: 'Completed the founding member feedback survey', shareMessage: 'Just earned Trail Blazer on Zephyr for helping shape the future of RV trip planning. 🛠️' },
            co_pilot: { name: 'Co-Pilot', emoji: '📡', description: 'Submitted a bug report or feature suggestion', shareMessage: 'Helping build @ZephyrTravel as a Co-Pilot. 📡 #RVLife' },
            road_reporter: { name: 'Road Reporter', emoji: '📸', description: 'Shared a trip report or photo', shareMessage: 'Just earned Road Reporter on Zephyr for sharing my trip. 📸 #ZephyrTravel #RVLife' },
            wind_chaser: { name: 'Wind Chaser', emoji: '🌬️', description: 'Planned your first wind-optimized route', shareMessage: 'Just earned Wind Chaser on Zephyr — planned my first wind-optimized RV route. 🌬️ #ZephyrTravel #RVLife' },
            mountain_goat: { name: 'Mountain Goat', emoji: '⛰️', description: 'Navigated a high elevation route', shareMessage: 'Just earned Mountain Goat on Zephyr for conquering a mountain pass. ⛰️ #ZephyrTravel #RVLife' },
            long_hauler: { name: 'Long Hauler', emoji: '🛣️', description: 'Planned a trip over 1,000 miles', shareMessage: 'Just earned Long Hauler on Zephyr — planned a 1,000+ mile RV trip. 🛣️ #ZephyrTravel #RVLife' },
            road_warrior: { name: 'Road Warrior', emoji: '🔁', description: 'Completed 10 trips planned in Zephyr', shareMessage: 'Just hit 10 trips on Zephyr and earned Road Warrior. 🔁 This app has changed how I RV. #ZephyrTravel #RVLife' },
            winter_wanderer: { name: 'Winter Wanderer', emoji: '❄️', description: 'Planned a trip during winter months', shareMessage: 'Earned Winter Wanderer on Zephyr — RVing doesn\'t stop in winter. ❄️ #ZephyrTravel #RVLife' },
            full_timer: { name: 'Full-Timer', emoji: '🏆', description: 'Used Zephyr every month for 12 consecutive months', shareMessage: 'Just earned Full-Timer on Zephyr — 12 months of smarter RV travel. 🏆 #ZephyrTravel #RVLife' }
        };

        // Show first badge (if multiple earned, show them sequentially)
        const badgeId = newBadges[0];
        const badge = badgeDefs[badgeId];
        if (!badge) return;

        const modal = document.getElementById('badgeNotificationModal');
        document.getElementById('badgeEmoji').textContent = badge.emoji;
        document.getElementById('badgeName').textContent = badge.name;
        document.getElementById('badgeDescription').textContent = w(badge.description);

        // Populate share card
        document.getElementById('shareCardEmoji').textContent = badge.emoji;
        document.getElementById('shareCardName').textContent = badge.name;
        document.getElementById('shareCardDesc').textContent = w(badge.description);

        // Share button
        const shareBtn = document.getElementById('badgeShareBtn');
        shareBtn.onclick = () => {
            if (navigator.share) {
                navigator.share({ text: w(badge.shareMessage) });
            } else {
                navigator.clipboard.writeText(w(badge.shareMessage));
                shareBtn.textContent = 'Copied!';
                setTimeout(() => { shareBtn.textContent = 'Share'; }, 2000);
            }
        };

        // Dismiss button — marks badges as seen
        const dismissBtn = document.getElementById('badgeDismissBtn');
        dismissBtn.onclick = () => {
            modal.style.display = 'none';
            // Mark badges as seen
            const user = window.authService?.getUser();
            if (user) {
                const backendUrl = import.meta.env.VITE_BACKEND_URL || 'https://6wdv4xnpei.execute-api.us-west-1.amazonaws.com/prod';
                fetch(`${backendUrl}/api/profile/badges/seen`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${window.authService?.getAccessToken()}`
                    },
                    body: JSON.stringify({ userId: user.sub })
                }).catch(err => console.warn('Failed to mark badges seen:', err));
            }

            // Show next badge if multiple earned
            if (newBadges.length > 1) {
                setTimeout(() => this.showBadgeNotification(newBadges.slice(1)), 500);
            }
        };

        modal.style.display = 'flex';
    }

    /**
     * Save the current route
     */
    async saveCurrentRoute() {
        const saveBtn = document.getElementById('saveRouteBtn');
        
        try {
            // Check if user is signed in
            if (!window.authService || !window.authService.isSignedIn()) {
                alert('Please sign in to save routes');
                window.authService.signIn();
                return;
            }

            const user = window.authService.getUser();
            if (!user) {
                alert('Please sign in to save routes');
                return;
            }

            const routeData = routeTrackingService.getCurrentRoute();
            if (!routeData) {
                alert('No route to save. Please calculate a route first.');
                return;
            }

            // Disable button and show loading
            if (saveBtn) {
                saveBtn.disabled = true;
                saveBtn.textContent = '💾 Saving...';
            }

            // Save route
            const result = await routeTrackingService.saveRoute(user.sub, routeData);

            if (result.success) {
                // Update route counter
                this.updateRouteCounter();

                // Show success message
                alert('✅ Route saved successfully!');

                // Show badge notification if new badges earned
                if (result.newBadges && result.newBadges.length > 0) {
                    this.showBadgeNotification(result.newBadges);
                }

                // Hide save button after successful save
                if (saveBtn) {
                    saveBtn.style.display = 'none';
                }
            }
        } catch (error) {
            console.error('Error saving route:', error);
            alert('Failed to save route. Please try again.');
        } finally {
            // Re-enable button
            if (saveBtn) {
                saveBtn.disabled = false;
                saveBtn.textContent = '💾 Save This Route';
            }
        }
    }

    /**
     * Update the usage counter display below the calculate button (legacy - kept for compatibility)
     */
    updateUsageDisplay() {
        // v1.3: Update the Google Maps button text to show launch counter
        this.updateGoogleMapsButton();
    }

    /**
     * Update the Google Maps button to show launch counter
     * v1.3: Counter lives on the button itself, not in the header
     */
    updateGoogleMapsButton() {
        const btn = document.getElementById('exportNavigation');
        if (!btn) return;

        btn.innerHTML = '�� Navigate with Zephyr';
        btn.style.opacity = '1';
        btn.style.pointerEvents = 'auto';
        btn.style.background = 'white';
        btn.style.color = '#1e88e5';
        btn.style.borderColor = '#1e88e5';

        // Phase 15: Keep the floating card launch button in sync
        if (window.updateCardLaunchButton) window.updateCardLaunchButton();
    }

    /**
     * Phase 6 Task 14: Suggest stops based on drive time preferences
     * @param {number} totalDistance - Total route distance in miles
     * @param {Array} segments - Route segments with distance info
     * @returns {Array} Stop suggestions
     */
    suggestStops(totalDistance, segments, coordinates = []) {
        const prefs = window.travelPreferences || {};
        const speed = prefs.avgCruisingSpeedMph || 60;
        const preferredHours = prefs.preferredDriveTimeHours || 6;
        const maxHours = prefs.maxDriveTimeHours || 8;
        const autoSuggest = prefs.autoSuggestStops !== false;

        if (!autoSuggest) return [];

        const totalHours = totalDistance / speed;
        const suggestions = [];

        // Only suggest if trip is long enough
        if (totalHours <= preferredHours) return [];

        // Helper: find coordinate at a given mile along the route
        const getCoordAtMile = (targetMile) => {
            if (!coordinates || coordinates.length < 2) return null;
            let dist = 0;
            for (let i = 1; i < coordinates.length; i++) {
                const prev = coordinates[i - 1];
                const curr = coordinates[i];
                const segDist = this.haversineDistance(prev.lat, prev.lng, curr.lat, curr.lng);
                dist += segDist;
                if (dist >= targetMile) return { lat: curr.lat, lng: curr.lng };
            }
            return null;
        };

        // Soft suggestion at preferred drive time
        if (totalHours > preferredHours) {
            const suggestAtMile = Math.round(speed * preferredHours);
            const coord = getCoordAtMile(suggestAtMile);
            suggestions.push({
                type: 'suggested',
                message: units.isMetric()
                    ? `Consider a stop around km ${units.distance(suggestAtMile)} — you'll have been driving ~${preferredHours} hours at ${units.speed(speed)} km/h.`
                    : `Consider a stop around mile ${suggestAtMile} — you'll have been driving ~${preferredHours} hours at ${speed}mph.`,
                nearestStop: null,
                atMile: suggestAtMile,
                coord
            });
        }

        // Hard warning at max drive time
        if (totalHours > maxHours) {
            const warnAtMile = Math.round(speed * maxHours);
            const coord = getCoordAtMile(warnAtMile);
            suggestions.push({
                type: 'warning',
                message: units.isMetric()
                    ? `You'll reach your ${maxHours}-hour drive limit around km ${units.distance(warnAtMile)}. A stop is strongly recommended.`
                    : `You'll reach your ${maxHours}-hour drive limit around mile ${warnAtMile}. A stop is strongly recommended.`,
                nearestStop: null,
                atMile: warnAtMile,
                coord
            });
        }

        return suggestions;
    }

    /**
     * Phase 5.5: Sample waypoints along route coordinates at a given mile interval
     * @param {Array<{lat, lng}>} coordinates - Route coordinates
     * @param {number} intervalMiles - Miles between samples (default 45)
     * @returns {Array<{lat, lng}>} Sampled waypoints
     */
    sampleRouteWaypoints(coordinates, intervalMiles = 45) {
        if (!coordinates || coordinates.length < 2) return [];

        const sampled = [];
        let distanceSinceLastSample = 0;

        for (let i = 1; i < coordinates.length; i++) {
            const prev = coordinates[i - 1];
            const curr = coordinates[i];
            const segmentMiles = this.haversineDistance(prev.lat, prev.lng, curr.lat, curr.lng);
            distanceSinceLastSample += segmentMiles;

            if (distanceSinceLastSample >= intervalMiles) {
                sampled.push({ lat: curr.lat, lng: curr.lng });
                distanceSinceLastSample = 0;
            }
        }

        return sampled;
    }

    /**
     * Phase 5.5: Merge priority waypoints with sampled waypoints, cap at max
     * Priority waypoints always included first, then fill with sampled
     * @param {Array} priority - Priority waypoints (fuel stops, passes)
     * @param {Array} sampled - Corridor waypoints from sampling
     * @param {number} max - Maximum total waypoints (Google Maps limit: 9)
     * @returns {Array<{lat, lng}>}
     */
    mergeAndCapWaypoints(priority, sampled, max = 9) {
        const merged = [];
        const seen = new Set();

        // Add priority waypoints first
        for (const wp of priority) {
            if (merged.length >= max) break;
            const key = `${wp.lat.toFixed(3)},${wp.lng.toFixed(3)}`;
            if (!seen.has(key)) {
                merged.push(wp);
                seen.add(key);
            }
        }

        // Fill remaining slots with sampled corridor waypoints
        for (const wp of sampled) {
            if (merged.length >= max) break;
            const key = `${wp.lat.toFixed(3)},${wp.lng.toFixed(3)}`;
            if (!seen.has(key)) {
                merged.push(wp);
                seen.add(key);
            }
        }

        return merged;
    }

    /**
     * Haversine distance between two points in miles
     */
    haversineDistance(lat1, lng1, lat2, lng2) {
        const R = 3959; // Earth radius in miles
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLng = (lng2 - lng1) * Math.PI / 180;
        const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                  Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                  Math.sin(dLng/2) * Math.sin(dLng/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        return R * c;
    }

    /**
     * Phase 17: Display low clearance warnings on map and route summary
     */
    displayClearanceWarnings(warnings) {
        console.log(`🚧 Displaying ${warnings.length} clearance warning(s)`);

        // Clear previous clearance markers
        if (this.mapService.clearanceMarkers) {
            this.mapService.clearanceMarkers.forEach(m => m.remove());
        }
        this.mapService.clearanceMarkers = [];

        // Add markers to map for each warning
        warnings.forEach(w => {
            const color = w.severity === 'danger' ? '#e53935' : 
                         w.severity === 'warning' ? '#FF9800' : '#FFC107';
            const icon = L.divIcon({
                html: `<div style="background: ${color}; color: white; border-radius: 50%; width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; font-size: 14px; border: 2px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3);">⚠️</div>`,
                className: 'clearance-marker',
                iconSize: [28, 28],
                iconAnchor: [14, 14]
            });

            const marker = L.marker([w.lat, w.lng], { icon, zIndexOffset: 2000 })
                .addTo(this.mapService.map);

            marker.bindPopup(`
                <div style="min-width: 200px;">
                    <strong style="color: ${color};">${w.severity === 'danger' ? '🚨 WILL NOT FIT' : w.severity === 'warning' ? '⚠️ VERY TIGHT' : '📋 TIGHT FIT'}</strong><br><br>
                    <strong>Clearance:</strong> ${w.clearanceFormatted}<br>
                    <strong>Your rig:</strong> ${this.clearanceService.formatHeight(w.rigHeightFt, 'up')}<br>
                    <strong>Margin:</strong> ${w.marginFt < 0 ? 'WILL NOT FIT' : units.isMetric() ? Math.max(0, units.height(w.clearanceFt, 'down') - units.height(w.rigHeightFt, 'up')).toFixed(1) + ' m' : Math.floor((w.clearanceFt - w.rigHeightFt) * 12 + 1e-9) + '"'}<br>
                    ${w.name !== 'Low clearance' ? `<br><em>${escapeHtml(w.name)}</em>` : ''}
                </div>
            `);

            this.mapService.clearanceMarkers.push(marker);
        });

        // Show warning banner in route info section
        const routeInfo = document.getElementById('routeInfo');
        if (routeInfo) {
            const existing = document.getElementById('clearanceBanner');
            if (existing) existing.remove();

            const dangerCount = warnings.filter(w => w.severity === 'danger').length;
            const warningCount = warnings.filter(w => w.severity === 'warning').length;
            const noticeCount = warnings.filter(w => w.severity === 'notice').length;

            const bannerColor = dangerCount > 0 ? '#e53935' : '#FF9800';
            const bannerIcon = dangerCount > 0 ? '🚨' : '⚠️';
            const bannerText = dangerCount > 0
                ? `${dangerCount} bridge(s) too low for your rig!`
                : `${warningCount + noticeCount} tight clearance(s) on this route`;

            const banner = document.createElement('div');
            banner.id = 'clearanceBanner';
            banner.style.cssText = `background: ${bannerColor}15; border: 2px solid ${bannerColor}; border-radius: 8px; padding: 0.75rem; margin-top: 1rem;`;
            banner.innerHTML = `
                <div style="font-weight: 600; color: ${bannerColor}; margin-bottom: 0.5rem;">${bannerIcon} Low Clearance ${dangerCount > 0 ? 'DANGER' : 'Warning'}</div>
                <div style="font-size: 0.85rem; color: #2c3e50;">${bannerText}</div>
                <ul style="margin: 0.5rem 0 0 1rem; font-size: 0.8rem; color: #546e7a; list-style: none; padding: 0;">
                    ${warnings.slice(0, 3).map(w => `<li style="margin-bottom: 0.25rem;">${w.severity === 'danger' ? '🚨' : '⚠️'} ${w.clearanceFormatted} — ${escapeHtml(w.name)}</li>`).join('')}
                </ul>
                ${warnings.length > 3 ? `<div style="font-size: 0.75rem; color: #888; margin-top: 0.25rem;">+ ${warnings.length - 3} more (check map)</div>` : ''}
            `;

            const exportBtn = document.getElementById('exportNavigation');
            if (exportBtn) {
                routeInfo.insertBefore(banner, exportBtn);
            } else {
                routeInfo.appendChild(banner);
            }
        }
    }

    /**
     * Phase 21: Display campground markers on the map
     */
    /**
     * Active road hazards reported within about a mile of this route
     * (🚧 Report a Road Hazard), one emoji pin each.
     */
    async loadRoadReports(coordinates) {
        if (!coordinates || coordinates.length < 2 || !this.mapService?.map) return;
        const backendUrl = import.meta.env.VITE_BACKEND_URL || 'https://6wdv4xnpei.execute-api.us-west-1.amazonaws.com/prod';
        let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
        for (const c of coordinates) {
            minLat = Math.min(minLat, c.lat); maxLat = Math.max(maxLat, c.lat);
            minLng = Math.min(minLng, c.lng); maxLng = Math.max(maxLng, c.lng);
        }
        const pad = 0.05;
        try {
            const query = `minLat=${minLat - pad}&maxLat=${maxLat + pad}&minLng=${minLng - pad}&maxLng=${maxLng + pad}`;
            const response = await fetch(`${backendUrl}/api/road-reports/active?${query}`);
            const data = await response.json();
            const step = Math.max(1, Math.floor(coordinates.length / 400));
            const sampled = coordinates.filter((_, i) => i % step === 0);
            const nearRoute = (data.reports || []).filter(r =>
                sampled.some(c => this.haversineDistance(r.lat, r.lng, c.lat, c.lng) <= 1));
            this.currentRoadReports = nearRoute;
            this.displayRoadReportMarkers(nearRoute);
        } catch (err) {
            console.warn('Road reports fetch failed (non-blocking):', err);
        }
    }

    displayRoadReportMarkers(reports) {
        if (!this.mapService?.map) return;
        if (this.roadReportLayer) this.roadReportLayer.clearLayers();
        else this.roadReportLayer = L.layerGroup().addTo(this.mapService.map);
        const ago = (iso) => {
            const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
            if (mins < 60) return `${mins} min ago`;
            const hours = Math.round(mins / 60);
            if (hours < 48) return `${hours} hr ago`;
            return `${Math.round(hours / 24)} days ago`;
        };
        for (const r of reports) {
            const icon = L.divIcon({
                html: `<div style="background: white; border: 2px solid #e65100; border-radius: 50%; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; font-size: 16px; box-shadow: 0 2px 6px rgba(0,0,0,0.3);">${r.emoji}</div>`,
                className: 'road-report-marker',
                iconSize: [30, 30],
                iconAnchor: [15, 15]
            });
            const clearance = r.clearanceFt && this.clearanceService
                ? `<br><strong>Clearance:</strong> ${this.clearanceService.formatHeight(r.clearanceFt, 'down')}` : '';
            const marker = L.marker([r.lat, r.lng], { icon, zIndexOffset: 1500 })
                .bindPopup(`
                    <div style="min-width: 200px;">
                        <strong>${r.emoji} ${escapeHtml(w(r.label))}</strong>
                        ${r.notes ? `<br>${escapeHtml(r.notes)}` : ''}
                        ${clearance}
                        <br><small style="color:#666;">Reported ${ago(r.submittedAt)}</small>
                    </div>
                `);
            this.roadReportLayer.addLayer(marker);
        }
    }

    displayCampgroundMarkers(campgrounds) {
        if (!this.mapService || !this.mapService.map) return;

        // Clear existing campground markers
        if (this.campgroundLayer) {
            this.campgroundLayer.clearLayers();
        } else {
            this.campgroundLayer = L.layerGroup().addTo(this.mapService.map);
        }

        // Icons created per-marker based on amenity count

        for (const cg of campgrounds) {
            if (!cg.lat || !cg.lng) continue;

            // Count amenities to determine icon size
            let amenityCount = 0;
            const h = cg.amenities?.hookups || {};
            if (h.electric) amenityCount++;
            if (h.water) amenityCount++;
            if (h.sewer) amenityCount++;
            if (cg.amenities?.pullThrough) amenityCount++;
            if (cg.amenities?.showers) amenityCount++;
            if (cg.amenities?.restrooms) amenityCount++;
            if (cg.amenities?.dumpStation) amenityCount++;
            if (cg.amenities?.potableWater) amenityCount++;
            if (cg.amenities?.wifi) amenityCount++;

            const isRich = amenityCount > 3;
            const hasRVHookups = (h.electricAmps && h.electricAmps >= 30);

            let campIcon;
            if (hasRVHookups) {
                // A div icon (same 32 px picture) so it carries the
                // campground-marker class the route card's switch hides;
                // LeafletShim gives image icons no class.
                campIcon = L.divIcon({
                    html: '<img src="/assets/ZephyrRV.png" style="width:32px;height:32px;" />',
                    className: 'campground-marker',
                    iconSize: [32, 32],
                    iconAnchor: [16, 16],
                    popupAnchor: [0, -16]
                });
            } else {
                campIcon = L.divIcon({
                    html: isRich ? '<span style="font-size:1.6rem;">🏕️</span>' : '<span style="font-size:1rem;opacity:0.7;">🏕️</span>',
                    className: 'campground-marker',
                    iconSize: isRich ? [32, 32] : [20, 20],
                    iconAnchor: isRich ? [16, 16] : [10, 10]
                });
            }

            const marker = L.marker([cg.lat, cg.lng], { icon: campIcon });

            // Australian parks come from each park's own website: power is
            // listed, water/sewer/rig length never are, and "verified" only
            // means the park published it, so no Verified tag or verify button.
            const isAu = cg.country === 'AU';

            const typeLabel = w(cg.type === 'rv_park' ? 'RV Park' : cg.type === 'federal' ? 'Federal' : cg.type === 'state' ? 'State Park' : 'Campground');
            const verified = isAu ? '' : (cg.verified ? '✓ Verified' : 'Unverified');
            const reserveLink = cg.amenities?.reservationUrl
                ? `<a href="${cg.amenities.reservationUrl}" target="_blank" style="display:block;text-align:center;background:#1e88e5;color:white;padding:0.5rem;border-radius:6px;text-decoration:none;font-weight:600;font-size:0.85rem;margin-top:0.6rem;">Reserve / Check Rates →</a>`
                : '';
            const description = cg.description ? `<p style="font-size:0.8rem;color:#555;margin:0.5rem 0;line-height:1.4;">${cg.description.substring(0, 150)}${cg.description.length > 150 ? '...' : ''}</p>` : '';

            // Hookup summary
            const hookups = cg.amenities?.hookups || {};
            let hookupText = '';
            if (isAu) hookupText = hookups.electric ? 'Powered sites' : '';
            else if (hookups.full) hookupText = 'Full Hookups';
            else if (hookups.electric && hookups.water) hookupText = 'Electric + Water';
            else if (hookups.electric) hookupText = 'Electric Only';
            else hookupText = 'No Hookups';
            if (hookups.electricAmps) hookupText += ` (${hookups.electricAmps}A)`;

            const maxRig = cg.amenities?.maxRigLengthFt ? (units.isMetric() ? `${(Math.floor(cg.amenities.maxRigLengthFt * 3.048) / 10).toFixed(1)} m max` : `${cg.amenities.maxRigLengthFt} ft max`) : '';

            // Amenity grid
            const amenityItems = [];
            if (hookups.electric && !isAu) amenityItems.push('⚡ Electric');
            if (hookups.water) amenityItems.push('💧 Water');
            if (hookups.sewer) amenityItems.push('🚰 Sewer');
            if (cg.amenities?.pullThrough) amenityItems.push(w('↔️ Pull-Thru'));
            if (cg.amenities?.showers) amenityItems.push('🚿 Showers');
            if (cg.amenities?.restrooms) amenityItems.push('🚻 Restrooms');
            if (cg.amenities?.dumpStation) amenityItems.push(w('🚽 Dump Station'));
            if (cg.amenities?.potableWater) amenityItems.push('🥤 Potable Water');
            if (cg.amenities?.wifi) amenityItems.push('📶 WiFi');
            if (cg.amenities?.petFriendly) amenityItems.push('🐕 Pet Friendly');
            if (cg.amenities?.campfires === true) amenityItems.push('🔥 Campfires');
            if (cg.amenities?.campfires === false) amenityItems.push('🚫 No Fires');
            if (cg.amenities?.pool) amenityItems.push('🏊 Pool');
            if (cg.amenities?.campKitchen) amenityItems.push('🍳 Camp Kitchen');
            if (cg.amenities?.laundry) amenityItems.push('🧺 Laundry');

            const phoneDigits = (cg.phone || '').replace(/[^\d+]/g, '');
            const phoneLine = phoneDigits
                ? `<div style="font-size:0.8rem;margin-top:0.4rem;"><a href="tel:${phoneDigits}" style="color:#1e88e5;text-decoration:none;">📞 ${cg.phone.replace(/[<>&"]/g, '')}</a></div>`
                : '';

            const amenityGrid = amenityItems.length > 0
                ? `<div style="display:flex;flex-wrap:wrap;gap:0.3rem 0.6rem;margin-top:0.4rem;font-size:0.75rem;color:#444;">${amenityItems.map(a => `<span>${a}</span>`).join('')}</div>`
                : '';

            const pricingNote = cg.pricing?.free ? '<span style="color:#22c55e;font-weight:600;">Free</span>' : (cg.pricing?.pricingNotes || '');

            const addStopBtn = `<button onclick="window.addGasStationAsStop(${cg.lat}, ${cg.lng}, '${cg.name.replace(/'/g, "\\'")}', 'cg_${cg.campgroundId}')" style="display:block;width:100%;text-align:center;background:#48bb78;color:white;padding:0.5rem;border:none;border-radius:6px;font-weight:600;font-size:0.85rem;margin-top:0.4rem;cursor:pointer;">+ Add as Stop</button>`;

            const verifyBtn = isAu ? '' : `<button onclick="window.verifyCampgroundFromPopup('${cg.campgroundId}')" style="display:block;width:100%;text-align:center;background:white;color:#1e88e5;padding:0.4rem;border:1px solid #1e88e5;border-radius:6px;font-weight:500;font-size:0.8rem;margin-top:0.3rem;cursor:pointer;">✓ Been here? Verify this info</button>`;

            const favoriteBtn = window.isSubscribed
                ? `<button onclick="window.saveFavoriteCampground('${cg.campgroundId}', '${cg.name.replace(/'/g, "\\'")}')" style="display:block;width:100%;text-align:center;background:white;color:#f59e0b;padding:0.4rem;border:1px solid #f59e0b;border-radius:6px;font-weight:500;font-size:0.8rem;margin-top:0.3rem;cursor:pointer;">${w('⭐ Save to Favorites')}</button>`
                : '';

            const popup = `
                <div style="min-width:250px;max-width:320px;font-family:-apple-system,sans-serif;">
                    <strong style="font-size:1rem;display:block;margin-bottom:0.2rem;">${cg.name}</strong>
                    <div style="font-size:0.8rem;color:#666;margin-bottom:0.3rem;">${typeLabel} · ${cg.address || cg.state}${verified ? ` · ${verified}` : ''}</div>
                    ${description}
                    <div style="background:#f8f9fa;border-radius:6px;padding:0.5rem 0.6rem;margin-top:0.4rem;">
                        ${hookupText ? `<div style="font-size:0.82rem;font-weight:600;color:#333;margin-bottom:0.2rem;">${hookupText}</div>` : ''}
                        ${maxRig ? `<div style="font-size:0.75rem;color:#666;margin-bottom:0.2rem;">🚐 Max Rig: ${maxRig}</div>` : ''}
                        ${amenityGrid}
                    </div>
                    ${pricingNote ? `<div style="font-size:0.8rem;margin-top:0.4rem;">${pricingNote}</div>` : ''}
                    ${phoneLine}
                    ${addStopBtn}
                    ${verifyBtn}
                    ${favoriteBtn}
                    ${reserveLink}
                </div>
            `;

            marker.bindPopup(popup);
            this.campgroundLayer.addLayer(marker);
        }

        console.log(`🏕️ ${campgrounds.length} campground markers added to map`);
    }

    /**
     * Phase 18: Display road quality in route trip card and map card
     */
    displayRoadQualityWarnings(roadQuality) {
        if (!roadQuality || !roadQuality.summary) return;

        const summary = roadQuality.summary || {};
        const roughCount = summary.roughSegments || 0;
        const overall = summary.overall || 'unknown';

        if (overall === 'unknown') return;

        console.log(`🛣️ Road quality: ${overall}, ${roughCount} rough spots`);

        // Determine color and label
        let ratingColor, ratingLabel;
        if (overall === 'excellent' || (overall === 'good' && roughCount === 0)) {
            ratingColor = '#4CAF50';
            ratingLabel = 'SMOOTH';
        } else if (overall === 'good') {
            ratingColor = '#4CAF50';
            ratingLabel = 'GOOD';
        } else if (overall === 'fair') {
            ratingColor = '#FFC107';
            ratingLabel = 'FAIR';
        } else {
            ratingColor = '#F44336';
            ratingLabel = 'ROUGH';
        }

        // Build Pro segment breakdown (only show rough segments on interstates/US routes)
        // Merge consecutive rough spots within 10 miles into ranges
        let segmentListHtml = '';
        if (roadQuality.segments && roadQuality.segments.length > 0) {
            const routeCoords = this.currentRoutes?.[this.bestRouteIndex]?.coordinates || [];
            const roughSegs = roadQuality.segments
                .filter(s => {
                    if (s.iriRating !== 'poor') return false;
                    if (!s.highway?.startsWith('I-') && !s.highway?.startsWith('US-')) return false;
                    return isSegmentOnRoute(s, routeCoords);
                })
                .sort((a, b) => {
                    if (a.highway !== b.highway) return a.highway.localeCompare(b.highway);
                    return (a.beginMile || 0) - (b.beginMile || 0);
                });

            // Merge segments on same highway within 10 miles of each other
            const merged = [];
            for (const seg of roughSegs) {
                const mile = Math.round(seg.beginMile || 0);
                const last = merged[merged.length - 1];
                if (last && last.highway === seg.highway && mile - last.endMile <= 10) {
                    last.endMile = Math.max(last.endMile, Math.round(seg.endMile || mile));
                } else {
                    merged.push({
                        highway: seg.highway,
                        startMile: mile,
                        endMile: Math.round(seg.endMile || mile)
                    });
                }
            }

            // Sort by route order (startMile) and cap at 10
            merged.sort((a, b) => a.startMile - b.startMile);
            const capped = merged.slice(0, 10);

            if (capped.length > 0) {
                segmentListHtml = `
                    <details style="margin-top: 0.75rem; border-top: 1px solid #e0e0e0; padding-top: 0.5rem;">
                        <summary style="font-size: 0.8rem; font-weight: 600; color: #546e7a; cursor: pointer;">Rough spots (${capped.length})</summary>
                        <div style="margin-top: 0.4rem;">
                        ${capped.map(r => {
                            const markerWord = units.isMetric() ? 'km' : 'Mile';
                            const at = (m) => units.isMetric() ? Math.round(units.distance(m)) : m;
                            const miles = r.startMile === r.endMile ? `${markerWord} ${at(r.startMile)}` : `${markerWord} ${at(r.startMile)}–${at(r.endMile)}`;
                            return `<div style="display: flex; justify-content: space-between; align-items: center; padding: 0.25rem 0; font-size: 0.8rem;">
                                <span>${r.highway} · ${miles}</span>
                                <span style="color: #ef4444; font-weight: 600;">Rough</span>
                            </div>`;
                        }).join('')}
                        </div>
                    </details>
                `;
            }
        }

        // Free users: show upgrade prompt when rough spots exist but no segment data
        let upgradeHtml = '';
        if (!roadQuality.segments && roughCount > 0) {
            upgradeHtml = `
                <div style="margin-top: 0.75rem; border-top: 1px solid #e0e0e0; padding-top: 0.5rem;">
                    <div style="font-size: 0.8rem; color: #546e7a; text-align: center; padding: 0.5rem;">
                        🔒 <strong>${roughCount} rough section${roughCount > 1 ? 's' : ''}</strong> on this route${canPurchaseInApp() ? `<br>
                        <span style="color: #1e88e5; cursor: pointer;" onclick="document.getElementById('subscribeBtn')?.click()">Upgrade to Pro</span> for map overlay + segment details` : ''}
                    </div>
                </div>
            `;
        }

        // Build the road quality card HTML (same style as wind/elevation)
        const cardHtml = `
            <div style="background: white; padding: 0.75rem; border-radius: 6px; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem;">
                    <strong style="font-size: 0.95rem;">🛣️ Road Quality</strong>
                    <span style="
                        background: ${ratingColor}; 
                        color: white; 
                        padding: 0.3rem 0.6rem; 
                        border-radius: 4px; 
                        font-size: 0.75rem;
                        font-weight: 700;
                        letter-spacing: 0.5px;
                    ">${ratingLabel}</span>
                </div>
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem; font-size: 0.85rem;">
                    <div><strong>Overall:</strong> ${overall === 'excellent' ? 'Smooth' : overall === 'good' ? 'Good' : overall === 'fair' ? 'Fair' : 'Rough'}</div>
                    <div><strong>Rough Spots:</strong> ${roughCount}</div>
                </div>
                ${segmentListHtml}
                ${upgradeHtml}
            </div>
        `;

        // Inject into the best route's trip card slot
        const slot = document.getElementById(`roadQualitySlot-${this.bestRouteIndex}`);
        if (slot) {
            slot.innerHTML = cardHtml;
        }

        // Also refresh the map card if it's visible
        if (window.updateRouteCard) {
            window.updateRouteCard();
        }

        // Remove the old standalone banner if it exists
        const oldBanner = document.getElementById('roadQualityBanner');
        if (oldBanner) oldBanner.remove();

        // Pro: Draw color-coded road quality overlay on map
        // Only draw segments that are near the actual route polyline
        if (roadQuality.segments && roadQuality.segments.length > 0 && this.mapService) {
            try {
                const routeCoords = this.currentRoutes?.[this.bestRouteIndex]?.coordinates || [];
                const onRouteSegments = roadQuality.segments.filter(s => s.startLat && s.startLng && isSegmentOnRoute(s, routeCoords));
                this.mapService.drawRoadQualityOverlay(onRouteSegments, routeCoords);
            } catch (e) {
                console.warn('Road quality overlay failed (non-blocking):', e.message);
            }
        }
    }
}
